import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { changeLanguage as i18nChangeLanguage } from '../i18n/index';

/* ---------------------------------------------------------------------------
 * Translation dictionaries
 * -------------------------------------------------------------------------*/

const translations: Record<string, Record<string, string>> = {
  en: {
    // Nav / sidebar
    'nav.dashboard': 'Dashboard',
    'nav.appointments': 'Appointments',
    'nav.services': 'Services',
    'nav.employees': 'Employees',
    'nav.promotions': 'Promotions',
    'nav.packages': 'Packages',
    // AB#2034 — bundle grouping in the owner calendar / appointments. "Bundle" = the
    // discounted service package (AB#893) a customer books in one visit. {name} is the
    // bundle name; wrap it in FSI/PDI (⁨ ⁩) at interpolation for bidi safety.
    'bundle.tag': 'Bundle',
    'bundle.groupAria': 'Bundle: {name}',
    'bundle.partOf': 'Part of {name}',
    'packages.title': 'Packages',
    'packages.sub': 'Boost revenue by bundling services — sell several together at an offer price so clients book more in a single visit.',
    'packages.new': 'New package',
    'packages.loading': 'Loading…',
    'packages.sessions': 'sessions',
    'packages.valid': 'Valid',
    'packages.empty.h': 'No packages yet',
    'packages.empty.p': 'Create your first prepaid bundle to start selling services together at a discount.',
    'packages.gate.h': 'Packages is a Pro feature',
    'packages.gate.p': 'Packages let you bundle services at an offer price so clients book more in one visit. Upgrade to Pro or Enterprise to create and sell packages.',
    'packages.gate.cta': 'Upgrade to Pro',
    'packages.newTitle': 'New package',
    'packages.editTitle': 'Edit package',
    'packages.field.name': 'Package name',
    'packages.field.services': 'Included services',
    'packages.col.service': 'Service',
    'packages.col.qty': 'Qty',
    'packages.col.unit': 'Unit price',
    'packages.noServices': 'No services yet. Add one below.',
    'packages.emptyServices': 'Add a service above to build your package.',
    'packages.searchService': 'Search a service…',
    'packages.addService': 'Add a service…',
    'packages.add': 'Add',
    'packages.qtyMinus': 'Decrease quantity',
    'packages.qtyPlus': 'Increase quantity',
    'packages.removeService': 'Remove service',
    'packages.field.regular': 'Regular price (auto)',
    'packages.field.package': 'Package price',
    'packages.field.savings': 'You save',
    'packages.field.duration': 'Valid for',
    'packages.unit.days': 'Days',
    'packages.unit.weeks': 'Weeks',
    'packages.unit.months': 'Months',
    'packages.field.showin': 'Show in',
    'packages.mobile': 'Mobile app',
    'packages.online': 'Online booking page',
    'packages.advanced': 'Advanced options',
    'packages.transferable': 'Allow transfer to another customer',
    'packages.maxPurchases': 'Max purchases',
    'packages.unlimited': 'Unlimited',
    'packages.save': 'Save package',
    'packages.cancel': 'Cancel',
    'packages.toast.created': 'Package created',
    'packages.toast.updated': 'Package updated',
    'packages.toast.deleted': 'Package removed',
    'packages.toast.saveFailed': "Couldn't save package",
    'packages.toast.deleteFailed': "Couldn't remove package",
    'packages.err.name': 'Enter a package name',
    'packages.err.items': 'Add at least one service',
    'packages.err.price': 'Package price must be lower than the regular price',
    'packages.servicesInPackage': 'Services in this package',
    'packages.itemsCount': 'services',
    'packages.branches': 'Available at branches',
    'packages.allBranches': 'Offered at all branches — pick some to limit it.',
    'packages.branchesHint': 'Offered only at the selected branches.',
    'packages.branchesAll': 'All branches',
    'packages.branchesSpecific': 'Specific branches',
    'packages.pickBranchHint': 'Pick at least one branch.',
    'packages.discount': 'Discount',
    'packages.tab.active': 'Active',
    'packages.tab.archived': 'Archived',
    'packages.emptyArchived.h': 'No archived packages',
    'packages.status.active': 'Active',
    'packages.status.scheduled': 'Scheduled',
    'packages.status.paused': 'Paused',
    'packages.status.ended': 'Ended',
    'packages.status.archived': 'Archived',
    'packages.pause': 'Pause',
    'packages.resume': 'Resume',
    'packages.edit': 'Edit',
    'packages.archive': 'Archive',
    'packages.restore': 'Restore',
    'packages.toast.archived': 'Package archived',
    'packages.toast.restored': 'Package restored — paused',
    'packages.salesEndsAt': 'Stops selling on',
    'packages.salesEndsAtHint': 'Optional — leave empty to sell with no end date.',
    'packages.salesEndsAtSet': 'Will stop selling after this date.',
    'packages.customerSaves': 'Customer saves',
    'packages.moreOptions': 'Visibility and advanced options',
    'packages.noMatches': 'No matching services',
    'packages.addServiceHint': 'Add a service — search by name',
    'nav.customers': 'Customers',
    'nav.payments': 'Payments',
    'nav.subscription': 'Subscription',
    'nav.loyalty': 'Loyalty',
    'nav.inventory': 'Inventory',
    'nav.referrals': 'Referrals',
    'nav.settings': 'Settings',
    'nav.profile': 'Profile',
    'nav.users': 'Users',
    'nav.owners': 'Owners',
    'nav.featureFlags': 'Feature Flags',
    'nav.auditLog': 'Audit Log',
    'nav.apiKeys': 'API Keys',
    'nav.sponsoredPlacements': 'Sponsored Placements',
    'nav.platformPromotions': 'Platform Promotions',
    'nav.categories': 'Categories',
    // AB#2066 P1-A (scoped owner-portal Arabic closure) — nav + isolated hubs.
    'nav.calendar': 'Calendar',
    'nav.myBusiness': 'My Business',
    'settings.title': 'Settings',
    'settings.section.account': 'Account management',
    'settings.card.accountSettings.title': 'Account settings',
    'settings.card.accountSettings.desc': 'Manage account settings and payment information',
    'settings.card.profile.title': 'Profile',
    'settings.card.profile.desc': 'View and edit your personal profile',
    'settings.card.payment.title': 'Payment settings',
    'settings.card.payment.desc': 'Manage payment method',
    'settings.card.branches.title': 'Branches',
    'settings.card.branches.desc': 'Manage multiple branches',
    'settings.section.notifications': 'Notifications',
    'settings.card.notifications.title': 'Notification preferences',
    'settings.card.notifications.desc': 'Manage email, in-app, and push notification settings',
    'mybiz.group.yourBusiness': 'Your Business',
    'mybiz.section.businessDetails': 'Business Details',
    'mybiz.section.branches': 'Branches',
    'mybiz.section.categories': 'Categories',
    'mybiz.section.services': 'Services',
    'mybiz.section.calendarSettings': 'Calendar Settings',
    'mybiz.group.team': 'Team',
    'mybiz.section.manageStaff': 'Manage Staff',
    'mybiz.aria.sections': 'My Business sections',
    // AB#2108 (Admin V1 P0-2) — create business / Submit to Devanza / approval banner. arabic-translator sign-off 2026-09-21 (AB#2108 Localization section).
    'mybiz.create.title': 'Create your business',
    'mybiz.create.body': 'Start with your salon\'s name and city. Next you\'ll add the details, a branch, hours and services, then submit your salon to Devanza for review.',
    'mybiz.create.name': 'Business name',
    'mybiz.create.country': 'Country',
    'mybiz.create.city': 'City',
    'mybiz.create.country.AE': 'United Arab Emirates',
    'mybiz.create.country.JO': 'Jordan',
    'mybiz.create.action': 'Create business',
    'mybiz.create.saving': 'Creating…',
    'mybiz.create.error.name': 'Enter your business name (3–100 characters).',
    'mybiz.create.error.city': 'Enter your city (2–100 characters).',
    'mybiz.create.success': 'Business created. Complete the sections, then submit it to Devanza.',
    'mybiz.create.failed': 'Couldn\'t create your business. Try again.',
    'mybiz.create.sessionFailed': 'Your business was created. Sign out and sign in again to continue.',
    'mybiz.submit.title': 'Submit your salon to Devanza',
    'mybiz.submit.body': 'When your salon is ready, submit it for review. Devanza reviews every salon before it goes live and will notify you when it\'s approved.',
    'mybiz.submit.item.businessDetails': 'Business details and hours',
    'mybiz.submit.item.branches': 'At least one active branch with its map location',
    'mybiz.submit.item.categories': 'At least one category',
    'mybiz.submit.item.services': 'At least one active service',
    'mybiz.submit.item.staff': 'At least one active staff member',
    'mybiz.submit.staffMissing': 'Add at least one active staff member who offers a service at an active branch, then submit again.',
    'mybiz.submit.itemDone': 'Done',
    'mybiz.submit.itemTodo': 'To do',
    'mybiz.submit.itemChecking': 'Checking',
    'mybiz.submit.itemUnknown': 'Not checked',
    'mybiz.submit.pinHint': 'Customers find your salon by its branch location, so set each branch\'s location on the map.',
    'mybiz.submit.consent': 'I confirm these details are accurate and I accept the Devanza Terms & Conditions and Privacy Policy.',
    'mybiz.submit.termsLink': 'Terms & Conditions',
    'mybiz.submit.privacyLink': 'Privacy Policy',
    'mybiz.submit.action': 'Submit to Devanza',
    'mybiz.submit.saving': 'Submitting…',
    'mybiz.submit.incomplete': 'Complete the items above to submit.',
    'mybiz.submit.checking': 'Checking your setup…',
    'mybiz.submit.checkFailed': 'Couldn\'t check your salon\'s setup.',
    'mybiz.submit.retry': 'Try again',
    'mybiz.submit.success': 'Submitted. Devanza will review your salon and notify you.',
    'mybiz.submit.failed': 'Couldn\'t submit your salon. Check your details and try again.',
    'approval.banner.pending': 'Your salon is pending approval. Devanza is reviewing it and will notify you when it\'s live.',
    'approval.banner.rejected': 'Devanza needs some changes before your salon can go live.',
    'approval.banner.reason': 'Reason:',
    'approval.banner.update': 'Update and resubmit',
    'payments.status.all': 'All Statuses',
    'payments.status.completed': 'Completed',
    'payments.status.pending': 'Pending',
    'payments.status.failed': 'Failed',
    'payments.status.refunded': 'Refunded',
    'nav.chat': 'Chat',
    'nav.help': 'Help',
    'nav.support': 'Support',
    // Header / menu
    'menu.accountSettings': 'Account settings',
    'menu.languages': 'Languages',
    'menu.theme': 'Theme',
    'menu.themeLight': 'Light',
    'menu.themeDark': 'Dark',
    'menu.termsAndConditions': 'Terms and Conditions',
    'menu.logout': 'Logout',
    'header.search': 'Search',
    // Toasts
    'toast.languageSet': 'Language set to English',
    'toast.languageSetAr': 'تم تغيير اللغة إلى العربية',
    'toast.themeSetLight': 'Switched to light theme',
    'toast.themeSetDark': 'Switched to dark theme',
    // AB#1818 — Super Admin Categories page
    'superadmin.categories.title': 'Categories',
    'superadmin.categories.subtitle': 'Manage marketplace categories shown on customer mobile',
    'superadmin.categories.new': 'New category',
    'superadmin.categories.tab.all': 'All',
    'superadmin.categories.tab.pending': 'Pending approval',
    'superadmin.categories.tab.inactive': 'Inactive',
    'superadmin.categories.status.approved': 'Approved',
    'superadmin.categories.status.pending': 'Pending',
    'superadmin.categories.status.rejected': 'Rejected',
    'superadmin.categories.status.active': 'Active',
    'superadmin.categories.status.inactive': 'Inactive',
    'superadmin.categories.column.image': 'Image',
    'superadmin.categories.column.name': 'Name',
    'superadmin.categories.column.nameAr': 'Name (Arabic)',
    'superadmin.categories.column.status': 'Status',
    'superadmin.categories.column.actions': 'Actions',
    'superadmin.categories.action.approve': 'Approve',
    'superadmin.categories.action.reject': 'Reject',
    'superadmin.categories.action.edit': 'Edit',
    'superadmin.categories.action.deactivate': 'Deactivate',
    'superadmin.categories.action.cancel': 'Cancel',
    'superadmin.categories.create.title': 'New category',
    'superadmin.categories.create.name': 'Name (English)',
    'superadmin.categories.create.nameAr': 'Name (Arabic)',
    'superadmin.categories.create.image': 'Image',
    'superadmin.categories.create.image.drop': 'Drop image here or click to browse',
    'superadmin.categories.create.image.hint': 'JPG, PNG, WebP · max 2 MB · recommended 264×264 px',
    'superadmin.categories.create.image.remove': 'Remove image',
    'superadmin.categories.create.activeNow': 'Active immediately',
    'superadmin.categories.create.autoApprovedNotice': 'Toggle "Active immediately" to publish now, or leave it off to keep the category hidden.',
    'superadmin.categories.create.submit': 'Create',
    'superadmin.categories.reject.title': 'Reject category',
    'superadmin.categories.reject.reason': 'Reason',
    'superadmin.categories.reject.reason.hint': 'Minimum 10 characters. Shown to the requesting Salon Owner.',
    'superadmin.categories.reject.reason.counter': '{count}/10 minimum',
    'superadmin.categories.reject.submit': 'Reject',
    'superadmin.categories.deactivate.title': 'Deactivate category',
    'superadmin.categories.deactivate.body': 'Deactivating hides this category from browsing. Services stay bookable and existing appointments are honoured. You can reactivate it later.',
    'superadmin.categories.deactivate.confirm': 'Deactivate',
    'superadmin.categories.deactivate.submit': 'Deactivate',
    'superadmin.categories.empty.all': 'No categories yet. Create the first one.',
    'superadmin.categories.empty.deactivated': 'No deactivated categories. Active categories show under All.',
    'superadmin.categories.toast.created': 'Category created',
    'superadmin.categories.toast.approved': 'Category approved',
    'superadmin.categories.toast.rejected': 'Category rejected',
    'superadmin.categories.toast.deactivated': 'Category deactivated',
    'superadmin.categories.toast.error': 'Something went wrong — please try again.',
    'superadmin.categories.error.duplicate': 'A category with this name already exists.',
    'superadmin.categories.error.imageTooLarge': 'Image is over 2 MB — pick a smaller one or compress before uploading.',
    'superadmin.categories.error.imageDimensions': 'Image is too small — upload one at least 200×200 px.',
    'superadmin.categories.error.imageType': 'Pick a JPG, PNG, or WebP image.',
    'superadmin.categories.error.fetch': 'Failed to load categories.',
    'superadmin.categories.error.nameRequired': 'Name is required (2-60 characters).',
    'superadmin.categories.error.nameArRequired': 'Arabic name is required (2-120 characters).',
    'superadmin.categories.error.imageRequired': 'An image is required.',
    'superadmin.categories.error.reasonRequired': 'Please enter at least 10 characters.',
    'superadmin.categories.retry': 'Retry',
    // AB#1872 — category lifecycle (edit / deactivate / reactivate)
    'superadmin.categories.tab.deactivated': 'Deactivated',
    'superadmin.categories.status.deactivated': 'Deactivated',
    'superadmin.categories.action.reactivate': 'Reactivate',
    'superadmin.categories.action.more': 'More actions',
    'superadmin.categories.edit.title': 'Edit category',
    'superadmin.categories.edit.submit': 'Save changes',
    'superadmin.categories.edit.renameReach': 'Renaming this category updates it for {count} subscribed salons.',
    'superadmin.categories.edit.image.replace': 'Drop a new image here or click to replace',
    'superadmin.categories.edit.image.current': 'Current image',
    'superadmin.categories.toast.updated': 'Category updated',
    'superadmin.categories.toast.deactivated.done': 'Category deactivated',
    'superadmin.categories.toast.reactivated': 'Category reactivated',
    // AB#1870 — gender targeting control + row pill
    'superadmin.categories.gender.label': 'Category gender targeting',
    'superadmin.categories.gender.none': 'No preference',
    'superadmin.categories.gender.female': 'Female',
    'superadmin.categories.gender.male': 'Male',
    'superadmin.categories.gender.hint': 'Most categories serve everyone. Set a target only when the category is specific to one gender.',
    'categories.target.female': 'For women',
    'categories.target.male': 'For men',
    // AB#1871 — deactivate-impact counts + lazy accordion drill-down
    'superadmin.categories.impact.salons': 'Subscribed salons',
    'superadmin.categories.impact.services': 'Services',
    'superadmin.categories.impact.bookings': 'Upcoming bookings',
    'superadmin.categories.impact.detail.salons': 'View affected salons',
    'superadmin.categories.impact.detail.services': 'View affected services',
    'superadmin.categories.impact.detail.bookings': 'View upcoming bookings',
    'superadmin.categories.impact.onlyCategory': 'only category',
    'superadmin.categories.impact.more': '+ {count} more',
    'superadmin.categories.impact.empty': 'No items',
    'superadmin.categories.impact.loadError': 'Could not load. Retry.',
    'superadmin.categories.impact.serviceFrom': 'From {price} {currency}',
    'superadmin.categories.deactivate.singleCategoryWarning': '{count} of these {total} salons have no other category and will disappear from category browsing until they re-categorize.',
    'superadmin.categories.deactivate.impactError': 'Could not load the impact summary. Try again before deactivating.',
    // ── Epic AB#1824 — Business Hours Matrix (Model E) — Dina copy table ──
    // Rows 1-50 of the design handoff. Mirrors the surfaces in §2 user
    // flows + §6 a11y labels. Both EN + AR strings authored by Dina;
    // arabic-translator sign-off required before merge.
    'hoursMatrix.tab.label': 'Hours',
    'hoursMatrix.title': 'Business Hours',
    'hoursMatrix.subtitle.single': '{salonName} · 1 branch',
    'hoursMatrix.subtitle.uniform': '{salonName} · {n} branches · All hours match',
    'hoursMatrix.subtitle.divergent': '{salonName} · {n} branches · {k} differ from default',
    'hoursMatrix.defaultRow.label': 'Default',
    'hoursMatrix.defaultRow.sub.single': 'Applies to all branches',
    'hoursMatrix.defaultRow.sub.uniform': 'Edits apply to {n} branches',
    'hoursMatrix.defaultRow.sub.divergent.one': '⚠ 1 cell diverges',
    'hoursMatrix.defaultRow.sub.divergent.other': '⚠ {k} cells diverge',
    'hoursMatrix.defaultRow.pill.single': 'SINGLE',
    'hoursMatrix.defaultRow.pill.uniform': 'ALL BRANCHES',
    'hoursMatrix.defaultRow.pill.divergent': 'EFFECTIVE',
    'hoursMatrix.cell.open': '{openTime} – {closeTime}',
    'hoursMatrix.cell.closed': 'Closed',
    'hoursMatrix.cell.matchesDefault': '(matches default)',
    'hoursMatrix.cell.differsFromDefault': 'Differs from default',
    'hoursMatrix.cell.unsaved': 'Unsaved',
    'hoursMatrix.cell.differsCount.one': '1 differs',
    'hoursMatrix.cell.differsCount.other': '{n} differ',
    'hoursMatrix.cell.stale': 'Stale',
    'hoursMatrix.editor.open': 'Open',
    'hoursMatrix.editor.close': 'Close',
    'hoursMatrix.editor.closedAllDay': 'Closed all day',
    'hoursMatrix.editor.save': 'Save',
    'hoursMatrix.editor.cancel': 'Cancel',
    'hoursMatrix.editor.openHour': 'Open hour',
    'hoursMatrix.editor.openMinute': 'Open minute',
    'hoursMatrix.editor.closeHour': 'Close hour',
    'hoursMatrix.editor.closeMinute': 'Close minute',
    'hoursMatrix.day.sun.short': 'Sun',
    'hoursMatrix.day.mon.short': 'Mon',
    'hoursMatrix.day.tue.short': 'Tue',
    'hoursMatrix.day.wed.short': 'Wed',
    'hoursMatrix.day.thu.short': 'Thu',
    'hoursMatrix.day.fri.short': 'Fri',
    'hoursMatrix.day.sat.short': 'Sat',
    'hoursMatrix.day.sun.long': 'Sunday',
    'hoursMatrix.day.mon.long': 'Monday',
    'hoursMatrix.day.tue.long': 'Tuesday',
    'hoursMatrix.day.wed.long': 'Wednesday',
    'hoursMatrix.day.thu.long': 'Thursday',
    'hoursMatrix.day.fri.long': 'Friday',
    'hoursMatrix.day.sat.long': 'Saturday',
    'hoursMatrix.column.branch': 'Branch',
    'hoursMatrix.branch.revertLink': '↺ Revert to default',
    'hoursMatrix.branch.revertAllLink': '↺ Revert all to default',
    'hoursMatrix.branch.hq': 'HQ',
    'hoursMatrix.branch.cellRevertTip': '↺ Revert',
    'hoursMatrix.branch.lastDivergenceNote': 'All branches now match — Default row will become the only editor on refresh',
    'hoursMatrix.revertConfirm.text': 'Revert {branch} · {day} to {newValue} (match default)?',
    'hoursMatrix.revertConfirm.apply': 'Revert',
    'hoursMatrix.revertConfirm.cancel': 'Cancel',
    'hoursMatrix.bulkModal.title.singleDay': 'Apply {day} hours to all {n} branches?',
    'hoursMatrix.bulkModal.title.multiDay': 'Apply changes to all {n} branches?',
    'hoursMatrix.bulkModal.subhead': 'Choose which branches to update.',
    'hoursMatrix.bulkModal.diff.deselected': 'deselected — will keep current hours',
    'hoursMatrix.bulkModal.summary': '{k} of {n} branches will update',
    'hoursMatrix.bulkModal.cancel': 'Cancel',
    'hoursMatrix.bulkModal.confirm': 'Apply to {k} branches',
    'hoursMatrix.bulkModal.confirm.one': 'Apply to 1 branch',
    'hoursMatrix.bulkModal.confirm.other': 'Apply to {k} branches',
    'hoursMatrix.bulkModal.deselectAll': 'Deselect all',
    'hoursMatrix.bulkModal.headsUp': 'Heads up: per-branch overrides will be replaced on selected branches.',
    'hoursMatrix.bulkModal.confirmLabel': 'Confirm bulk apply',
    'hoursMatrix.banner.concurrency.title': 'Someone else updated these hours',
    'hoursMatrix.banner.concurrency.body': '{editorName} updated {branchScope} hours {timeAgo}. Your draft was not saved. Review the latest version and try again.',
    'hoursMatrix.banner.concurrency.bodyGeneric': 'Another user updated these hours. Your draft was not saved. Review the latest version and try again.',
    'hoursMatrix.banner.concurrency.review': 'Review changes',
    'hoursMatrix.banner.concurrency.discard': 'Discard my draft',
    'hoursMatrix.banner.concurrency.dismiss': 'Dismiss',
    'hoursMatrix.banner.concurrency.scope.branch': '{branch}',
    'hoursMatrix.banner.concurrency.scope.multiple': '{count} branches',
    // T5: plural agreement for the multi-branch scope phrase.
    'hoursMatrix.banner.concurrency.scope.multiple.one': '1 branch',
    'hoursMatrix.banner.concurrency.scope.multiple.other': '{count} branches',
    'hoursMatrix.staffConflict.title': '{day} shifts will not be bookable',
    'hoursMatrix.staffConflict.body.matrix': '⚠️ {day} shifts won\'t appear in availability because {branch} is closed {day}. Update branch hours to enable.',
    'hoursMatrix.staffConflict.body.modal': '⚠️ {n} staff schedules affected ({sampleNames}). Save proceeds, but shifts on this day will not be bookable.',
    'hoursMatrix.staffConflict.viewStaffCta': 'View staff schedules',
    'hoursMatrix.branchCard.readonlyHeader': 'Effective hours',
    'hoursMatrix.branchCard.readonlyHeader.matchesDefault': '· same as Default',
    'hoursMatrix.branchCard.readonlyHeader.differs.one': '· 1 day differs from Default',
    'hoursMatrix.branchCard.readonlyHeader.differs.other': '· {n} days differ from Default',
    'hoursMatrix.branchCard.editLink': 'Edit in Business Hours →',
    'hoursMatrix.branchCard.explainer': 'Hours are now managed from Business Details. Branch cards are read-only summaries — edit once in the matrix and changes apply wherever you choose.',
    'hoursMatrix.branchCard.divergesNote': '· differs from default ({defaultValue})',
    'hoursMatrix.toast.success.singleBranch': '{branch} · {day} hours updated',
    'hoursMatrix.toast.success.bulk': '{day} hours updated on {k} branches',
    'hoursMatrix.toast.success.bulk.one': '{day} hours updated on 1 branch',
    'hoursMatrix.toast.success.bulk.other': '{day} hours updated on {k} branches',
    'hoursMatrix.toast.success.bulkGeneric': 'Hours updated on {k} branches',
    'hoursMatrix.toast.success.bulkGeneric.one': 'Hours updated on 1 branch',
    'hoursMatrix.toast.success.bulkGeneric.other': 'Hours updated on {k} branches',
    'hoursMatrix.toast.revertSuccess': '{branch} · {day} reverted to default',
    'hoursMatrix.toast.postSaveShiftsWarning.one': 'Saved. 1 {day} shift at {branch} is now unbookable.',
    'hoursMatrix.toast.postSaveShiftsWarning.other': 'Saved. {n} {day} shifts at {branch} are now unbookable.',
    'hoursMatrix.toast.error.generic': 'Couldn\'t save hours. Please try again.',
    'hoursMatrix.toast.error.staleHours': 'Someone else updated Hours. Refreshed — please review and save again.',
    'hoursMatrix.toast.error.branchNotFound': 'A branch was deleted while you were editing. Refreshed to the latest version.',
    'hoursMatrix.toast.error.validation': 'Some cells have invalid times. Please fix them and try again.',
    'hoursMatrix.emptyState.noBranches': 'Add your first branch on Branches to start setting hours.',
    'hoursMatrix.loading': 'Loading hours…',
    'hoursMatrix.error.fetch': 'Couldn\'t load business hours.',
    'hoursMatrix.error.retry': 'Try again',
    'hoursMatrix.actions.cancel': 'Cancel',
    'hoursMatrix.actions.save': 'Save',
    'hoursMatrix.actions.saving': 'Saving…',
    'hoursMatrix.foot.singleHint': 'Click any day to edit · changes apply only to {salonName}',
    'hoursMatrix.foot.uniformHint': 'Editing Default will prompt to apply to all {n} branches',
    'hoursMatrix.foot.divergentHint': 'Click any cell to edit · per-branch edits never trigger bulk apply',
    'hoursMatrix.foot.lastDivergenceHint': 'If this is the last divergence, matrix will collapse to uniform on next refresh',
    'hoursMatrix.foot.staleHint': 'Save is disabled while concurrent edits exist',
    'hoursMatrix.divergence.chip.one': '1 differs from default',
    'hoursMatrix.divergence.chip.other': '{k} differ from default',
    'hoursMatrix.uniform.chip': 'All match',
    'hoursMatrix.legend.open': 'Open',
    'hoursMatrix.legend.closed': 'Closed',
    'hoursMatrix.legend.divergent': 'Divergent from default',
    'hoursMatrix.legend.stale': 'Stale cell — your draft differs from latest server version',
    'hoursMatrix.a11y.grid': 'Business hours editor, {n} branches, use arrow keys to move between cells',
    'hoursMatrix.a11y.defaultRow': 'Default hours row. Applies to all branches when uniform.',
    'hoursMatrix.a11y.branchRow': '{branch} hours row. {k} day(s) differ from default.',
    'hoursMatrix.a11y.cell.divergent': '{day}, {branch}, {open}–{close}, differs from default. Press Enter to edit.',
    'hoursMatrix.a11y.cell.open': '{day}, {branch}, {open} to {close}. Press Enter to edit.',
    'hoursMatrix.a11y.cell.closed': '{day}, {branch}, closed. Press Enter to edit.',
    'hoursMatrix.a11y.cell.matchesDefault': '{day}, {branch}, matches default. Press Enter to edit.',
    'hoursMatrix.a11y.banner': 'Concurrent edit conflict alert',
    'hoursMatrix.a11y.bulkModal': 'Bulk apply confirmation',
    'hoursMatrix.a11y.readonlyTable': 'Effective hours for {branch} · read only',
    'hoursMatrix.branchesStrip.label.one': '1 branch all match',
    'hoursMatrix.branchesStrip.label.other': '{n} branches all match',
    'hoursMatrix.branchesStrip.expandHint': 'Click to expand per-branch view',
    'hoursMatrix.branchesStrip.collapseHint': 'Click to collapse to default-only view',
    'rejection.branchClosed.toast': 'The branch is closed on this day.',
    'rejection.branchClosed.bannerTitle': '{branch} is closed on {day}.',
    'rejection.branchClosed.bannerBody': 'Pick a different date. To change opening hours, go to Business Details → Hours.',
    // AB#1840 (PBI-B) / AB#1838 — Customer-side gender matrix vocabulary.
    // Wiki §3 canonical labels — bilingual. Render label + glyph everywhere
    // the gender axis surfaces (service cards, salon cards, filter chips,
    // booking sheets). Mixed/Everyone is rendered explicitly per founder
    // Q6 = A (2026-05-31).
    'services.gender.menOnly': 'Men only',
    'services.gender.womenOnly': 'Women only',
    'services.gender.mixed': 'Mixed',
    'services.gender.aria.menOnly': 'For men only',
    'services.gender.aria.womenOnly': 'For women only',
    'services.gender.aria.mixed': 'For everyone',
    // AB#1847 (PBI-I) / AB#1838 — Customer-side age-group matrix vocabulary.
    // Wiki §3 canonical labels. Adult is the silent default (no chip on
    // service cards) — only Kid + Both surface visually. Form labels are
    // required: the owner must explicitly pick a value on Add/Edit Service.
    // AR `Both` = `كلاهما` per founder Q1 (2026-05-31) — NOT `الجميع`,
    // which is reserved for the gender axis `Everyone` label.
    'services.ageGroup.label': 'Who is this service for?',
    'services.ageGroup.adults': 'Adults',
    'services.ageGroup.kids': 'Kids',
    'services.ageGroup.both': 'Both',
    // AB#1899 — Services form "Copy from another service" auto-suffix.
    // When the owner picks a source from the Copy-from dropdown the
    // name pre-fills as `<source> (copy)` to avoid the BE per-branch +
    // per-category name-uniqueness 400. Collisions escalate to
    // `(copy 2)`, `(copy 3)`, etc. — interpolated via .replace('{n}', n).
    // The noun "copy" is followed by the counter; the noun itself is
    // unchanged across n.
    'services.copyFrom.suffix.first': '(copy)',
    'services.copyFrom.suffix.nth': '(copy {n})',
    'services.ageGroup.cardKids': 'Kids',
    'services.ageGroup.cardBoth': 'Both',
    'services.ageGroup.aria.kids': 'For kids',
    'services.ageGroup.aria.both': 'For all ages',
    'services.ageGroup.required': 'Please pick who this service is for',
    // PBI 1962 (AB#1968) — Flexible service option groups (variants & add-ons).
    // Behind the REACT_APP_SERVICE_OPTION_GROUPS_ENABLED flag (default OFF).
    // AR strings are the committee-signed canonical set (PBI 1962 Localization
    // section) — do NOT re-translate.
    'services.builder.title': 'Option groups',
    'services.builder.intro': 'Configure the option groups customers choose from. Variant groups are pick-one tiers. Add-on groups are optional extras. Variant prices are absolute; add-on prices are added on top of the chosen variant.',
    'services.builder.variant': 'Variant',
    'services.builder.addon': 'Add-on',
    'services.builder.required': 'Required',
    'services.builder.optional': 'Optional',
    'services.builder.implicitBadge': 'Auto-wrapped (legacy)',
    'services.builder.addGroup': 'Add option group',
    'services.builder.addOption': 'Add option',
    'services.builder.groupName': 'Group name',
    'services.builder.groupNameAr': 'Group name (Arabic)',
    'services.builder.groupNamePlaceholder': 'e.g. Length',
    'services.builder.kind': 'Type',
    'services.builder.selection': 'Selection',
    'services.builder.selectionSingle': 'Single choice',
    'services.builder.selectionMultiple': 'Multiple choice',
    'services.builder.minSelect': 'Min',
    'services.builder.maxSelect': 'Max',
    'services.builder.optionName': 'Option name',
    'services.builder.optionNameAr': 'Option name (Arabic)',
    'services.builder.priceAbsolute': 'Price',
    'services.builder.priceDelta': 'Added price',
    'services.builder.duration': 'Duration',
    'services.builder.durationMinutes': 'min',
    'services.builder.requiredToggle': 'Required',
    'services.builder.removeGroup': 'Remove group',
    'services.builder.removeOption': 'Remove option',
    'services.builder.moveUp': 'Move up',
    'services.builder.moveDown': 'Move down',
    'services.builder.groupsCount': '{n} / 8 groups',
    'services.builder.optionsCount': '{n} / 20 options',
    'services.builder.empty': 'No option groups yet. Add one to let customers pick variants and extras.',
    // Client-side validation messages (mirror the backend validators).
    'services.builder.error.oneVariant': 'A service can have exactly one Variant group.',
    'services.builder.error.groupNameRequired': 'Group name is required.',
    'services.builder.error.optionNameRequired': 'Option name is required.',
    'services.builder.error.priceNonNegative': 'Price must be zero or more.',
    'services.builder.error.durationNonNegative': 'Duration must be zero or more.',
    'services.builder.error.minMax': 'Min cannot be greater than Max.',
    'services.builder.error.maxOneOption': 'Add at least one option to this group.',
    'services.builder.error.tooManyGroups': 'A service can have at most 8 option groups.',
    'services.builder.error.tooManyOptions': 'A group can have at most 20 options.',
    'services.builder.error.singleCurrency': 'All options in a service must use the same currency.',
    // Booking-side option validation (also used on mobile) — signed set.
    'error.option.variantRequired': 'Please choose one option.',
    'error.option.selectionInvalid': 'Your selection is invalid. Please review your choices.',
    // PBI 1962 (AB#1962) — Owner manual-booking option picker (AddEditAppointment).
    // Reuses the committee-signed services.builder.* / error.option.* strings;
    // the keys below are the picker-only additions. AR drafted gender-neutral
    // (verbal nouns / يُرجى) — flagged for /arabic-translator committee sign-off.
    'appointments.options.total': 'Total',
    'appointments.options.chooseAtLeast': 'Choose at least {n}',
    'appointments.options.chooseAtMost': 'Choose at most {n}',
    'appointments.options.upTo': 'Up to {n}',
    'appointments.options.serverTotalNote': 'Calculated from the selected options. The final total is confirmed by the server.',
    // AB#1845 (PBI-E) / AB#1838 — Staff gender becomes required at invite
    // time so the customer-side gender matrix can publish the staff member.
    // EN copy mirrors the BE `STAFF_GENDER_REQUIRED` validator message so
    // owners see one phrasing whether the FE or BE catches the miss.
    'staff.fields.gender': 'Gender',
    'staff.fields.gender.option.male': 'Male',
    'staff.fields.gender.option.female': 'Female',
    'staff.errors.genderRequired': 'Staff gender is required.',
    // Batch B (AB#2176) — staff record without a login; portal invitation is optional.
    'staff.add.subtitleRecord': 'Add a team member so customers can book them. Portal access is optional: add an email and phone number to invite them.',
    'staff.add.submit': 'Add staff',
    'staff.add.submitAndInvite': 'Add and invite',
    'staff.contact.emailOptional': 'Email (optional, for portal access)',
    'staff.contact.phoneOptional': 'Phone number (optional, for portal access)',
    'staff.contact.inviteNeedsBoth': 'To invite to the portal, enter both an email and a phone number.',
    'staff.contact.invalidEmail': 'Invalid email format',
    'staff.edit.inviteHint': 'No portal access yet. Enter an email and phone number to invite them.',
    'staff.status.noPortal': 'No portal access',
    'staff.status.invitePending': 'Invitation pending',
    'staff.toast.added': 'Staff member added.',
    'staff.toast.invited': 'Invitation sent to {email}.',
    'staff.toast.inviteFailed': 'Staff member saved, but the invitation could not be sent. {reason}',
    'staff.saving': 'Saving…',
    'staff.banner.completeGender': "Please complete this staff member's gender",
    'staff.banner.completeGender.cta': 'Edit',
    // AB#2005 (follow-up to AB#2004) — EmployeeModal chrome i18n. The modal
    // consumes useLanguage() (this LanguageContext dict), NOT i18next, so these
    // live here beside the existing staff.* keys — matching how AB#2004 homed
    // the weekday labels. Reuses staff.fields.gender[.option.*] above. AR
    // drafted gender-neutral (verbal nouns) — /arabic-translator sign-off.
    'staff.modal.addTitle': 'Add Employee',
    'staff.modal.editTitle': 'Edit Employee',
    'staff.modal.sendingInvitation': 'Sending invitation...',
    'staff.modal.saving': 'Saving...',
    'staff.modal.sending': 'Sending...',
    'staff.modal.add': 'Add',
    'staff.modal.save': 'Save',
    'staff.modal.cancel': 'Cancel',
    'staff.modal.delete': 'Delete employee',
    'staff.fields.fullName': 'Full Name',
    'staff.fields.fullName.placeholder': 'Employee Full Name',
    'staff.fields.email': 'Email',
    'staff.fields.email.placeholder': 'Email Address',
    'staff.fields.phone': 'Phone Number',
    'staff.fields.phone.placeholder': 'Phone Number',
    'staff.fields.position': 'Position',
    'staff.fields.position.placeholder': 'e.g. Stylist, Manager, etc.',
    'staff.fields.gender.placeholder': 'Select gender',
    'staff.fields.role': 'Role',
    'staff.fields.role.placeholder': 'Select role',
    'staff.fields.role.option.manager': 'Staff Manager',
    'staff.fields.role.option.staff': 'Staff',
    'staff.status.active': 'Active',
    'staff.status.inactive': 'Inactive',
    'staff.schedule.title': 'Work Schedule',
    'staff.schedule.notAvailable': 'Not Available',
    'staff.errors.fullNameRequired': 'Full name is required',
    'staff.errors.emailRequired': 'Email is required',
    'staff.errors.emailInvalid': 'Invalid email format',
    'staff.errors.phoneRequired': 'Phone number is required',
    // AB#1842 (PBI-F) / AB#1838 — Salon-gender hard-block: invite-modal
    // constraint visualization, ServiceType-flip blocker dialog, and the
    // Super Admin override flow. `{n}` / `{type}` / `{gender}` / `{opposite}`
    // are interpolated at render-time via a simple .replace() call (the t()
    // function itself is interpolation-free — see LanguageContext line 712).
    'staff.invite.genderTooltip.womenOnly': 'This salon is configured as Women only. Only female staff can be invited.',
    'staff.invite.genderTooltip.menOnly': 'This salon is configured as Men only. Only male staff can be invited.',
    'staff.errors.genderMismatchesSalon': "This staff member's gender doesn't match the salon configuration.",
    // AB#1862 — Booking-conflict guard on staff reassignment.
    //
    // Copy block authored by Dina (UX) and signed off by Faris in the
    // arch-brief §6.3. Tone: factual, owner-language only. No "orphan",
    // "ghost", "FK constraint". `{count}` / `{earliest}` / `{handler}` are
    // interpolated at render-time via `String.replace()` (the t() function
    // itself is interpolation-free — see LanguageContext line 837).
    'staff.reassign.conflict.title': 'Upcoming bookings affected',
    'staff.reassign.conflict.body': "This staff has {count} upcoming appointment(s) at the location(s) you're removing. The earliest is {earliest}. Continuing will leave those bookings on the calendar but mark the staff as no longer assigned to those branches.",
    'staff.reassign.conflict.body.services': "This staff has {count} upcoming appointment(s) for the service(s) you're unassigning. The earliest is {earliest}. Continuing will leave those bookings on the calendar but mark the staff as no longer able to perform those services.",
    'staff.reassign.conflict.viewCalendar': 'Review on calendar',
    // AB#1976 (CR-2026-07-04) — Staff absence management. AR strings pending
    // arabic-translator committee sign-off (batched follow-up before merge).
    'calendar.staffMenu.aria': "{name}'s actions",
    'calendar.staffMenu.bookWith': 'Book with {name}…',
    'calendar.staffMenu.addBreak': 'Add break / time off…',
    'calendar.staffMenu.viewStatus': 'View availability',
    // AB#2022 — colour-coded availability on the calendar.
    'calendar.absence.unavailable': 'Unavailable — {type}. You can’t book here.',
    'calendar.legend.onLeave': 'On leave (full day)',
    'calendar.legend.onBreak': 'On break',
    // AB#2023 — at-a-glance availability pills + "who's free at" check.
    'calendar.avail.available': 'Available',
    'calendar.avail.onLeave': 'On leave today',
    'calendar.avail.off': 'Off today',
    'calendar.avail.onBreak': 'On break · until {time}',
    'calendar.avail.booked': 'Booked',
    'calendar.avail.nextFree': 'next free {time}',
    'calendar.avail.offHours': 'Off now',
    'calendar.avail.reason.break': 'On break',
    'calendar.avail.reason.leave': 'On leave',
    'calendar.avail.reason.booked': 'Booked',
    'calendar.avail.reason.off': 'Off',
    'calendar.avail.checkLabel': 'Who’s free at',
    'calendar.avail.checkClear': 'Clear',
    'calendar.staffMenu.viewSchedule': "View {name}'s full schedule",
    // AB#2019 — read-only staff availability status panel.
    'staff.status.title': 'Availability',
    'staff.status.today': 'Today',
    'staff.status.upcoming': 'Upcoming',
    'staff.status.working': 'Working {range}',
    'staff.status.off': 'Not scheduled today',
    'staff.status.noUpcoming': 'No upcoming leave or breaks',
    'staff.status.loading': 'Loading…',
    'staff.status.close': 'Close',
    // AB#2021 — quick edit/remove of today's breaks from the panel.
    'staff.status.editBreak': 'Edit break',
    'staff.status.removeBreak': 'Remove break',
    'staff.status.removeConfirm': 'Remove this break?',
    'calendar.staffMenu.manage': 'Manage {name}',
    // P0-3 (AB#2043) — appointment status operations from the calendar peek popover.
    'calendar.peek.updateStatus': 'Update status',
    'calendar.peek.status.arrived': 'Arrived',
    'calendar.peek.status.started': 'Started',
    'calendar.peek.status.completed': 'Completed',
    'calendar.peek.status.noshow': 'No-show',
    'calendar.peek.statusUpdated': 'Status updated',
    'calendar.peek.statusUpdateFailed': 'Could not update status',
    'calendar.peek.close': 'Close',
    // AB#2164 — owner Confirm / Decline / Cancel booking from the calendar peek popover.
    'calendar.peek.action.confirm': 'Confirm',
    'calendar.peek.action.decline': 'Decline',
    'calendar.peek.action.cancel': 'Cancel booking',
    'calendar.peek.action.keepBooking': 'Keep booking',
    'calendar.peek.statusConfirmed': 'Confirmed',
    'calendar.peek.declinePrompt': 'Decline this booking? The customer will be notified.',
    'calendar.peek.cancelPrompt': 'Cancel this booking? The customer will be notified.',
    'calendar.peek.toast.confirmed': 'Booking confirmed',
    'calendar.peek.toast.declined': 'Booking declined',
    'calendar.peek.toast.cancelled': 'Booking cancelled',

    // O1 (AB#2199) — shared booking lifecycle words (founder terminology 2026-11-04).
    'booking.status.pending': 'Awaiting confirmation',
    'booking.status.confirmed': 'Confirmed',
    'booking.status.arrived': 'Arrived',
    'booking.status.started': 'Started',
    'booking.status.completed': 'Completed',
    'booking.status.noShow': 'No-show',
    'booking.status.cancelled': 'Cancelled',
    'booking.status.declinedBySalon': 'Declined by salon',
    'booking.status.cancelledByCustomer': 'Cancelled by customer',
    'booking.status.cancelledBySalon': 'Cancelled by salon',
    'booking.status.unknown': 'Unknown status',
    'booking.reason.expired': 'The request was not confirmed before the appointment time.',
    'booking.reason.admin': 'Cancelled by Devanza support.',
    'booking.detail.title': 'Booking details',
    'booking.detail.customer': 'Customer',
    'booking.detail.phone': 'Phone',
    'booking.detail.date': 'Date',
    'booking.detail.time': 'Time',
    'booking.detail.staff': 'Team member',
    'booking.detail.branch': 'Branch',
    'booking.detail.payment': 'Payment',
    'booking.detail.walkIn': 'Walk-in',
    'booking.payment.paid': 'Paid',
    'booking.payment.unpaid': 'Unpaid',
    'booking.payment.refunded': 'Refunded',
    'booking.action.markPaid': 'Mark as paid',
    'booking.action.openCalendar': 'Open in calendar',
    'calendar.loading': 'Loading the calendar…',
    'app.loading': 'Loading…',
    'session.expired': 'Your session has ended. Please sign in again.',
    'calendar.unassigned': 'Unassigned',
    'calendar.unassignedHint': 'Not on today\'s team schedule',
    'calendar.noStaff': 'No staff at this branch',
    'calendar.card.awaitingConfirmation': 'Awaiting confirmation',
    'common.comingSoon': 'Coming soon',
    'staff.absence.title': 'Breaks and time off',
    'staff.absence.add': 'Add absence',
    'staff.absence.editBreak': 'Edit break',
    'staff.absence.loading': 'Loading…',
    'staff.absence.empty': 'No breaks or time off set. Add one to manage availability.',
    'staff.absence.remove': 'Remove',
    'staff.absence.recurring': 'Repeats daily',
    'staff.absence.save': 'Add absence',
    'staff.absence.cancel': 'Cancel',
    'staff.absence.type.break': 'Break',
    'staff.absence.type.sick': 'Sick leave',
    'staff.absence.type.vacation': 'Vacation',
    'staff.absence.type.personal': 'Personal time',
    'staff.absence.type.emergency': 'Emergency',
    'staff.absence.reason.lunch': 'Lunch',
    'staff.absence.reason.prayer': 'Prayer (Salat)',
    'staff.absence.reason.rest': 'Rest',
    'staff.absence.reason.medical': 'Medical',
    'staff.absence.reason.other': 'Other',
    'staff.absence.field.type': 'Type',
    'staff.absence.field.branch': 'Branch',
    'staff.absence.field.date': 'Date',
    'staff.absence.field.startTime': 'Start time',
    'staff.absence.field.endTime': 'End time',
    'staff.absence.field.startDate': 'Start date',
    'staff.absence.field.endDate': 'End date',
    'staff.absence.field.reason': 'Reason',
    'staff.absence.field.recurring': 'Repeat every day',
    'staff.absence.toast.saved': 'Absence added',
    'staff.absence.toast.saveFailed': "Couldn't save absence",
    'staff.absence.toast.deleted': 'Absence removed',
    'staff.absence.toast.deleteFailed': "Couldn't remove absence",
    'staff.absence.error.branchRequired': 'Pick a branch for this absence',
    'staff.absence.error.dateOrder': 'End date must be on or after the start date',
    'staff.absence.error.timeOrder': 'End time must be after start time',
    'staff.absence.hours.works': 'Working hours: {range}',
    'staff.absence.hours.off': 'Off this day',
    'staff.absence.hours.warnOutside': "This is outside the staff member's working hours that day.",
    'staff.absence.error.outsideHours': "This time is outside the staff member's working hours that day. Pick a time within their hours, or update their schedule.",
    'staff.absence.error.overlap': 'This staff member already has an absence that overlaps this time. Adjust or remove it first.',
    'staff.absence.error.overlapDetail': 'Overlaps an existing absence — {details}. Remove or adjust it first.',
    'staff.absence.error.leaveOverlap': 'This staff member already has leave scheduled over these dates — {details}. Pick dates that don’t overlap it.',
    // AB#2024 — break policy caps (per branch).
    'staff.absence.break.allowance': 'Break allowance today: {used}/{total} min · {n} of {max} · this break: {this} min',
    'staff.absence.break.tooLong': 'A break can’t exceed {max} minutes. ',
    'staff.absence.break.switchToPersonal': 'Switch to Personal leave',
    'staff.absence.break.overLimit.title': 'Over the break limit',
    'staff.absence.break.overLimit.body': 'This exceeds the branch break limit ({maxBreaks} break / {maxMin} min per day). {used} min already used today. Add anyway?',
    // AB#2024 — per-branch break & absence rules editor.
    'branch.absencePolicy.button': 'Break rules',
    'branch.absencePolicy.title': 'Break & absence rules',
    'branch.absencePolicy.loading': 'Loading…',
    'branch.absencePolicy.maxBreaks': 'Breaks per day',
    'branch.absencePolicy.maxBreaks.help': 'How many separate breaks a staff member can take in a day.',
    'branch.absencePolicy.maxTotal': 'Total break time per day (min)',
    'branch.absencePolicy.maxTotal.help': 'Total break minutes allowed per staff each day.',
    'branch.absencePolicy.maxSingle': 'Longest single break (min)',
    'branch.absencePolicy.maxSingle.help': 'A break longer than this must be logged as Personal leave.',
    'branch.absencePolicy.override': 'Allow owners to override limits',
    'branch.absencePolicy.override.help': 'Owners can exceed these limits with a confirmation.',
    'branch.absencePolicy.save': 'Save rules',
    'branch.absencePolicy.cancel': 'Cancel',
    'branch.absencePolicy.saved': 'Break rules saved',
    'branch.absencePolicy.saveFailed': 'Couldn’t save break rules',
    'staff.absence.conflict.title': 'Bookings in this window',
    'staff.absence.conflict.body': 'This staff member has {count} booking(s) during this absence. Saving will leave them without a provider — reassign or cancel them first.',
    'staff.absence.conflict.more': '+{count} more',
    'staff.absence.conflict.saveAnyway': 'Save anyway',
    'staff.absence.conflict.cancel': 'Cancel',
    'staff.reassign.conflict.forceProceed': 'Continue anyway',
    'staff.reassign.conflict.cancel': 'Cancel',
    'staff.reassign.conflict.forcedToast': "Saved. {count} booking(s) remain on the staff's calendar — review them on Calendar.",
    'staff.reassign.conflict.deleteTitle': 'Cannot delete — bookings on file',
    'staff.reassign.conflict.deleteBody': 'This staff has {count} upcoming appointment(s) (earliest {earliest}). Cancel or reassign them before deleting this staff.',
    'staff.reassign.conflict.deleteClose': 'Got it',
    'staff.reassign.conflict.networkRetry': 'Could not save. Please check your connection and try again.',
    // Business Settings — ServiceType flip blocker dialog.
    'business.serviceType.flip.blocker.title.womenOnly': 'Cannot save: {n} male staff blocking this change',
    'business.serviceType.flip.blocker.title.menOnly': 'Cannot save: {n} female staff blocking this change',
    'business.serviceType.flip.blocker.body': 'Switching to "{type}" requires that all staff match that gender. The following staff don\'t match:',
    'business.serviceType.flip.blocker.instructions': 'Resolve by removing or transferring them, then try again.',
    'business.serviceType.flip.blocker.cta.resolve': 'Resolve first → Manage staff',
    'business.serviceType.flip.blocker.cta.cancel': 'Cancel',
    'business.serviceType.flip.blocker.override.link': 'Request Super Admin override',
    // Override request modal (owner side).
    'business.serviceType.flip.override.title': 'Request Super Admin override',
    'business.serviceType.flip.override.prompt': 'Why does your salon need an exception? (At least 20 characters.)',
    'business.serviceType.flip.override.placeholder': 'Explain the role and why an exception is appropriate…',
    'business.serviceType.flip.override.disclaimer': "A Devanza administrator will review your request. We'll email you when it's decided.",
    'business.serviceType.flip.override.submit': 'Submit request',
    'business.serviceType.flip.override.cancel': 'Cancel',
    'business.serviceType.flip.override.tooShort': 'Please write at least 20 characters explaining the exception.',
    'business.serviceType.flip.override.counter': '{count} / 20 minimum',
    'business.serviceType.flip.override.submitted': "Your override request has been submitted. We'll reply within 2 business days.",
    'business.serviceType.flip.override.error': 'Could not submit the request. Please try again.',
    // ServiceType labels (shared with the blocker body interpolation).
    'business.serviceType.womenOnly': 'Women only',
    'business.serviceType.menOnly': 'Men only',
    'business.serviceType.everyone': 'Mixed',
    // Super Admin console — gender override queue + audit log.
    'nav.genderOverrides': 'Gender Overrides',
    'superAdmin.overrides.title': 'Gender mismatch overrides',
    'superAdmin.overrides.subtitle': 'Owner requests to keep a staff member whose gender doesn\'t match the salon configuration.',
    'superAdmin.overrides.tab.pending': 'Pending',
    'superAdmin.overrides.tab.history': 'History',
    'superAdmin.overrides.empty.pending': 'No pending override requests.',
    'superAdmin.overrides.empty.history': 'No decisions recorded yet.',
    'superAdmin.overrides.loading': 'Loading overrides…',
    'superAdmin.overrides.error': 'Could not load overrides. Please refresh and try again.',
    'superAdmin.overrides.row.business': 'Business',
    'superAdmin.overrides.row.staff': 'Staff',
    'superAdmin.overrides.row.requestedBy': 'Requested by',
    'superAdmin.overrides.row.justification': 'Justification',
    'superAdmin.overrides.row.decidedBy': 'Decided by',
    'superAdmin.overrides.row.decidedAt': 'Decided',
    'superAdmin.overrides.row.adminReason': 'Reason',
    'superAdmin.overrides.action.approve': 'Approve',
    'superAdmin.overrides.action.reject': 'Reject',
    'superAdmin.overrides.status.pending': 'Pending',
    'superAdmin.overrides.status.approved': 'Approved',
    'superAdmin.overrides.status.rejected': 'Rejected',
    'superAdmin.overrides.decide.approve.title': 'Approve this override?',
    'superAdmin.overrides.decide.reject.title': 'Reject this override?',
    'superAdmin.overrides.decide.reasonLabel': 'Reason (at least 20 characters)',
    'superAdmin.overrides.decide.reasonPlaceholder': 'Why is this decision appropriate? (Shown to the owner.)',
    'superAdmin.overrides.decide.counter': '{count} / 20 minimum',
    'superAdmin.overrides.decide.confirm.approve': 'Approve override',
    'superAdmin.overrides.decide.confirm.reject': 'Reject override',
    'superAdmin.overrides.decide.cancel': 'Cancel',
    'superAdmin.overrides.decide.tooShort': 'Please write at least 20 characters explaining the decision.',
    'superAdmin.overrides.decide.toast.approved': 'Override approved.',
    'superAdmin.overrides.decide.toast.rejected': 'Override rejected.',
    'superAdmin.overrides.decide.toast.error': 'Could not save your decision. Please try again.',
    // ── AB#2045 — SuperAdmin "Grant founding access" (comp subscription) ──
    // Provisions a founding/free complimentary subscription so a salon can add
    // more than one branch. Plan brand names (Starter/Growth/Pro/Enterprise)
    // stay in Latin script per the plan brand-name convention.
    'superadmin.owners.grant.action.open': 'Grant founding access',
    'superadmin.owners.grant.title': 'Grant founding access',
    'superadmin.owners.grant.forOwner': 'Provisioning a complimentary subscription for',
    'superadmin.owners.grant.business': 'Business',
    'superadmin.owners.grant.planTier': 'Plan tier',
    'superadmin.owners.grant.planTier.hint': 'Enterprise lifts the branch limit so the salon can run multiple branches.',
    'superadmin.owners.grant.compDays': 'Duration (days)',
    'superadmin.owners.grant.compDays.hint': 'Length of the complimentary window, 1–3650 days. Default is 365.',
    'superadmin.owners.grant.reason': 'Reason (optional)',
    'superadmin.owners.grant.reason.placeholder': 'e.g. Founding partner, launch pilot',
    'superadmin.owners.grant.compNotice': 'This is a complimentary, non-billed grant. No card is charged and it is excluded from revenue reporting.',
    'superadmin.owners.grant.noBusiness': 'This owner has no business yet, so there is nothing to provision.',
    'superadmin.owners.grant.action.cancel': 'Cancel',
    'superadmin.owners.grant.action.submit': 'Grant access',
    'superadmin.owners.grant.toast.granted': 'Founding access granted.',
    'superadmin.owners.grant.toast.already': 'This business already has complimentary access.',
    'superadmin.owners.grant.toast.error': 'Could not grant access. Please try again.',
    'superadmin.owners.grant.error.conflict': 'This business already has a paid subscription, so a complimentary grant was not applied.',
    // ── AB#2112 — Super Admin activate / deactivate account (confirmation) ──
    'superadmin.accountStatus.title.deactivate': 'Deactivate account?',
    'superadmin.accountStatus.title.activate': 'Activate account?',
    'superadmin.accountStatus.account': 'Account',
    'superadmin.accountStatus.body.deactivate': "It will be signed out on every device straight away and can't be used to sign in until it is activated again.",
    'superadmin.accountStatus.body.activate': 'It can be used to sign in again. The person needs to sign in again: earlier sessions stay ended.',
    'superadmin.accountStatus.ownerNote': 'The salon is not affected: its listing and bookings stay as they are. To stop the salon itself, use Salon status.',
    'superadmin.accountStatus.action.cancel': 'Cancel',
    'superadmin.accountStatus.action.close': 'Close',
    'superadmin.accountStatus.action.deactivate': 'Deactivate',
    'superadmin.accountStatus.action.activate': 'Activate',
    'superadmin.accountStatus.toast.deactivated': 'Account deactivated.',
    'superadmin.accountStatus.toast.activated': 'Account activated.',
    'superadmin.accountStatus.toast.unchanged': 'No change: the account already had that status.',
    'superadmin.accountStatus.error.self': "You can't deactivate your own account.",
    'superadmin.accountStatus.error.lastSuperAdmin': 'At least one active Super Admin account must remain.',
    'superadmin.accountStatus.error.notFound': 'This account no longer exists. Refresh the list.',
    'superadmin.accountStatus.error.generic': "Couldn't update the account. Please try again.",
    // P0-7 (AB#2047) — SuperAdmin read-only "Recent bookings" support panel (EN).
    'superadmin.bookings.action.open': 'View bookings',
    'superadmin.bookings.title': 'Recent bookings',
    'superadmin.bookings.forOwner': 'Bookings for',
    'superadmin.bookings.business': 'Business',
    'superadmin.bookings.close': 'Close',
    'superadmin.bookings.retry': 'Retry',
    'superadmin.bookings.loading': 'Loading bookings…',
    'superadmin.bookings.empty': 'No bookings found for this business.',
    'superadmin.bookings.error': 'Could not load bookings. Please try again.',
    'superadmin.bookings.col.dateTime': 'Date & time',
    'superadmin.bookings.col.service': 'Service',
    'superadmin.bookings.col.staff': 'Staff',
    'superadmin.bookings.col.customer': 'Customer',
    'superadmin.bookings.col.phone': 'Phone',
    'superadmin.bookings.col.status': 'Status',
    'superadmin.bookings.col.amount': 'Amount',
    'superadmin.bookings.status.booked': 'Booked',
    'superadmin.bookings.status.approved': 'Approved',
    'superadmin.bookings.status.completed': 'Completed',
    'superadmin.bookings.status.cancelled': 'Cancelled',
    'superadmin.bookings.status.noshow': 'No-show',
    'superadmin.bookings.status.arrived': 'Arrived',
    'superadmin.bookings.status.started': 'Started',
    // Batch C (AB#2195) — Super Admin booking support, salon suspension, owner suspension banner (EN).
    'nav.bookingSupport': 'Booking Support',
    'approval.banner.suspended': 'Devanza has suspended your salon. Customers can\'t find it or book it right now. Your existing bookings stay on your calendar. Contact Devanza support if you have questions.',
    'superadmin.users.searchPlaceholder': 'Search by name, email or phone…',
    'superadmin.support.title': 'Booking support',
    'superadmin.support.subtitle': 'Find a customer\'s booking by booking number or phone, and cancel it if needed.',
    'superadmin.support.search.bookingNumber': 'Booking number',
    'superadmin.support.search.phone': 'Customer phone',
    'superadmin.support.search.phoneHint': 'Any format, e.g. +971 50 123 4567 or 050 123 4567.',
    'superadmin.support.search.submit': 'Search',
    'superadmin.support.search.clear': 'Clear',
    'superadmin.support.search.prompt': 'Enter a booking number or a customer phone to find a booking.',
    'superadmin.support.search.empty': 'No bookings match this search.',
    'superadmin.support.search.error.required': 'Enter a booking number or a customer phone.',
    'superadmin.support.search.error.bookingNumber': 'The booking number can only contain digits.',
    'superadmin.support.search.error.phone': 'Enter at least 7 digits of the phone number.',
    'superadmin.support.search.error.generic': 'Couldn\'t search bookings. Please try again.',
    'superadmin.support.col.booking': 'Booking',
    'superadmin.support.col.salon': 'Salon',
    'superadmin.support.col.actions': 'Actions',
    'superadmin.support.action.cancel': 'Cancel booking',
    'superadmin.support.cancel.title': 'Cancel this booking?',
    'superadmin.support.cancel.body': 'The salon will be told that Devanza support cancelled it, and so will the customer if they booked from their Devanza account. The time becomes free again. This can\'t be undone.',
    'superadmin.support.reason.label': 'Reason',
    'superadmin.support.reason.hint': 'Required. Kept in the audit log; not shown to the customer or the salon.',
    'superadmin.support.cancel.reason.placeholder': 'e.g. Salon closed for an emergency',
    'superadmin.support.cancel.keep': 'Keep booking',
    'superadmin.support.cancel.confirm': 'Cancel booking',
    'superadmin.support.cancel.toast.done': 'Booking cancelled. The salon has been notified.',
    'superadmin.support.cancel.error.notOpen': 'This booking can no longer be cancelled because it is no longer pending or confirmed. Refresh the list.',
    'superadmin.support.cancel.error.reasonRequired': 'Enter a reason.',
    'superadmin.support.cancel.error.reasonTooLong': 'The reason can be at most 500 characters.',
    'superadmin.support.cancel.error.notFound': 'This booking no longer exists. Refresh the list.',
    'superadmin.support.cancel.error.generic': 'Couldn\'t cancel the booking. Please try again.',
    'superadmin.salonStatus.action.open': 'Salon status',
    'superadmin.salonStatus.title': 'Salon status',
    'superadmin.salonStatus.current': 'Status',
    'superadmin.salonStatus.status.live': 'Live',
    'superadmin.salonStatus.status.suspended': 'Suspended',
    'superadmin.salonStatus.status.pending': 'Pending approval',
    'superadmin.salonStatus.status.draft': 'Draft',
    'superadmin.salonStatus.status.rejected': 'Changes requested',
    'superadmin.salonStatus.suspend.body': 'Suspending hides the salon from customers and stops new bookings straight away. The owner is told, with your reason. Existing bookings aren\'t cancelled: they\'re listed here for you to handle.',
    'superadmin.salonStatus.suspend.reason.placeholder': 'e.g. Repeated customer complaints',
    'superadmin.salonStatus.suspend.reason.hint': 'Required. The salon owner will see this reason, so don\'t include customer details.',
    'superadmin.salonStatus.reinstate.body': 'Reinstating makes the salon visible and bookable again. The owner is told.',
    'superadmin.salonStatus.notLiveNote': 'Only a live salon can be suspended. This salon isn\'t live.',
    'superadmin.salonStatus.action.suspend': 'Suspend salon',
    'superadmin.salonStatus.action.reinstate': 'Reinstate salon',
    'superadmin.salonStatus.action.suspending': 'Suspending…',
    'superadmin.salonStatus.action.reinstating': 'Reinstating…',
    'superadmin.salonStatus.owner': 'Owner',
    'superadmin.support.cancel.cancelling': 'Cancelling…',
    'superadmin.salonStatus.toast.suspended': 'Salon suspended. The owner will be notified.',
    'superadmin.salonStatus.toast.reinstated': 'Salon reinstated. The owner will be notified.',
    'superadmin.salonStatus.openBookings.title': 'Upcoming open bookings',
    'superadmin.salonStatus.openBookings.hint': 'These bookings weren\'t cancelled. Contact the customers, or cancel any booking that can\'t go ahead.',
    'superadmin.salonStatus.openBookings.empty': 'No upcoming open bookings.',
    'superadmin.salonStatus.error.notLive': 'Only a live salon can be suspended. Refresh the list.',
    'superadmin.salonStatus.error.notSuspended': 'This salon isn\'t suspended. Refresh the list.',
    'superadmin.salonStatus.error.reasonRequired': 'Enter a reason.',
    'superadmin.salonStatus.error.notFound': 'This salon no longer exists. Refresh the list.',
    'superadmin.salonStatus.error.generic': 'Couldn\'t update the salon. Please try again.',
    // ── AB#1904 (W-1) — Stripe Checkout upgrade flow (EN) ──────────────
    // Includes UpgradeConfirmModal, CheckoutRedirectingState, SuccessPage
    // (5 sub-states), CancelledPage (+ 4-chip survey), PostUpgradeBanner,
    // UnlockedFeatureCard titles, plan-cap refreshed copy, and
    // EnterpriseContactModal. ~78 keys total.
    // Interpolation placeholders ({planName}, {planId}, {amount}, {currency},
    // {n}, {newCap}, {oldPlanName}, {email}, {session_id}) are resolved
    // via String.replace() at render time — the t() function itself does
    // a flat key lookup; no token substitution built in.
    'subscription.confirm.title': 'Upgrade to {planName}',
    'subscription.confirm.currentLabel': 'Your plan today',
    'subscription.confirm.targetLabel': 'New plan',
    'subscription.confirm.delta': '+{currency} {delta}/mo',
    'subscription.confirm.prorationHead': 'Charged today',
    'subscription.confirm.prorationBody': "You'll be charged a prorated amount today for the remainder of your billing period. From your next invoice, you'll be charged {currency} {targetMonthly} per month.",
    'subscription.confirm.stripeNote': "You'll be redirected to Stripe's secure checkout to complete payment.",
    'subscription.confirm.continue': 'Continue to Checkout',
    'subscription.confirm.cancel': 'Cancel',
    'subscription.confirm.submitting': 'Opening secure checkout…',
    'subscription.confirm.errorMint': "We couldn't open checkout. Please try again. If this keeps happening, contact support.",
    'subscription.confirm.alreadyOnPlan': "You're already on {planName}.",
    'subscription.confirm.close': 'Close',
    'subscription.redirecting.title': 'Opening secure checkout…',
    'subscription.redirecting.sub': "You're being securely redirected to Stripe.",
    'subscription.success.verifyingTitle': 'Confirming your upgrade…',
    'subscription.success.verifyingBody': 'This usually takes a few seconds.',
    'subscription.success.title': 'Welcome to {planName}!',
    'subscription.success.sub': "Your new plan is active. Here's what you've just unlocked:",
    'subscription.success.ctaMyBusiness': 'Go to My Business',
    'subscription.success.ctaDashboard': 'Go to Dashboard',
    'subscription.success.receiptFooter': 'A receipt has been emailed to {email}. View past invoices anytime on your',
    'subscription.success.receiptFooterLink': 'Subscription page',
    'subscription.success.processingTitle': "Your payment was successful — we're just waiting for confirmation.",
    'subscription.success.processingSub': 'This sometimes takes a few seconds. The page will refresh automatically.',
    'subscription.success.pollAttempt': 'Attempt {n} of 15',
    'subscription.success.timeoutTitle': 'Almost there — your upgrade is still being processed.',
    'subscription.success.timeoutBody': "Your payment went through, but we haven't received confirmation from our payment processor yet. This usually resolves within a minute.",
    'subscription.success.timeoutRefresh': 'Refresh now',
    'subscription.success.timeoutContinue': 'Continue to Dashboard',
    'subscription.success.timeoutFooter': 'If your plan still shows as {oldPlanName} after a minute, contact the Devanza team with this reference: {session_id}.',
    'subscription.success.errorTitle': "We couldn't confirm your upgrade.",
    'subscription.success.errorBody': "Stripe completed your payment, but we couldn't link it to your account automatically. Don't worry — no charge has been lost.",
    'subscription.success.errorContact': 'Contact support',
    // AB#2133 — pilot support path (founder-configured contact; no hard-coded address).
    'support.contact.title': 'Contact the Devanza team',
    'support.contact.emailUs': 'Email us',
    'support.contact.whatsappUs': 'WhatsApp us',
    'support.contact.notConfigured': 'Our support contact is being set up. Please contact your Devanza account manager.',
    'help.faq.contactSupport.pilotAnswer': 'Email the Devanza support team using the contact details at the bottom of this page (also under Support in the sidebar).',
    'help.needMoreHelp.pilotBody': 'Can’t find your answer? Contact the Devanza support team:',
    'subscription.success.errorBack': 'Back to plans',
    'subscription.cancelled.title': "No problem — you weren't charged.",
    'subscription.cancelled.sub': 'Your {planName} plan continues as before. Take your time, the upgrade option will be here whenever you’re ready.',
    'subscription.cancelled.ctaBack': 'Back to plans',
    'subscription.cancelled.ctaDashboard': 'Go to Dashboard',
    'subscription.cancelled.surveyCaption': 'What stopped you? (Optional)',
    'subscription.cancelled.reasonPrice': 'Price too high',
    'subscription.cancelled.reasonThinking': 'Need to think about it',
    'subscription.cancelled.reasonFeatures': 'Missing features',
    'subscription.cancelled.reasonOther': 'Other',
    'subscription.cancelled.surveyThanks': 'Thanks — that helps us improve.',
    'subscription.banner.title': 'Welcome to {planName}!',
    'subscription.banner.cta': 'Explore your new tools →',
    'subscription.banner.dismissAria': 'Dismiss welcome banner',
    'subscription.unlock.branchesTitle': 'More branches',
    'subscription.unlock.branchesSub': 'Up to {newCap} locations',
    // Founder R-5 (CR-2026-06-10) — Growth tier branches cap is null (unlimited).
    // Separate key avoids ungrammatical "Up to Unlimited locations" / AR "حتى بلا حدود موقعاً".
    'subscription.unlock.branchesSubUnlimited': 'Unlimited locations',
    'subscription.unlock.staffTitle': 'Bigger team',
    'subscription.unlock.staffSub': 'Up to {newCap} staff',
    // Defensive — for any future tier where staff cap is unlimited (e.g. Enterprise).
    'subscription.unlock.staffSubUnlimited': 'Unlimited staff',
    'subscription.unlock.promotionsTitle': 'Promotions',
    'subscription.unlock.promotionsSub': 'Unlimited custom offers',
    'subscription.unlock.analyticsBasicTitle': 'Basic analytics',
    'subscription.unlock.analyticsBasicSub': 'Revenue, bookings, top services',
    'subscription.unlock.analyticsAdvTitle': 'Advanced analytics',
    'subscription.unlock.analyticsAdvSub': 'Cohorts, retention, forecasting',
    'subscription.unlock.loyaltyTitle': 'Loyalty program',
    'subscription.unlock.loyaltySub': 'Points, tiers, rewards',
    'subscription.unlock.rbacTitle': 'Roles & permissions',
    'subscription.unlock.rbacSub': 'Fine-grained staff access',
    'subscription.cap.branchesTitle': 'Ready for branch #{n}?',
    'subscription.cap.branchesBody': 'Your {plan} plan includes {cap} branches. Upgrade to {nextPlan} to manage up to {nextCap} — each with its own hours, staff and bookings.',
    // Pro → Enterprise transition where next tier is unlimited; avoids
    // ungrammatical "Upgrade to Enterprise to manage up to Infinity".
    'subscription.cap.branchesBodyUnlimited': 'Your {plan} plan includes {cap} branches. Upgrade to {nextPlan} for unlimited branches — each with its own hours, staff and bookings.',
    'subscription.cap.staffTitle': 'Ready for teammate #{n}?',
    'subscription.cap.staffBody': 'Your {plan} plan includes {cap} staff. Upgrade to {nextPlan} to add up to {nextCap}.',
    'subscription.cap.staffBodyUnlimited': 'Your {plan} plan includes {cap} staff. Upgrade to {nextPlan} for unlimited staff.',
    'subscription.cap.cta': 'Compare plans →',
    'subscription.recommendedRibbon': 'Recommended for you',
    'subscription.enterprise.title': "Let's design your Enterprise plan",
    'subscription.enterprise.sub': 'Enterprise plans are tailored to your team size, branches and integrations. Tell us about your business and our team will reach out within one business day.',
    'subscription.enterprise.branchesField': 'Estimated number of branches',
    'subscription.enterprise.staffField': 'Estimated team size',
    'subscription.enterprise.messageField': 'Anything else we should know? (Optional)',
    'subscription.enterprise.submit': 'Request a call',
    'subscription.enterprise.cancel': 'Cancel',
    'subscription.enterprise.successTitle': 'Thanks — sales will reach out within one business day.',
    'subscription.enterprise.successClose': 'Close',

    // ── AB#1982 — Owner Dashboard AR/EN i18n coverage ─────────────────────
    // Arabic values are a DRAFT pending arabic-translator committee sign-off
    // (AB#1982 AC-2). Western Arabic numerals (0-9) kept in both locales to
    // match the money-formatting convention (platformPromotions.utils §0).
    'dashboard.greeting': 'Hi, {name}',
    'dashboard.subtitle': "Here's what's happening {period}",
    'dashboard.period.today': 'today',
    'dashboard.period.week': 'this week',
    'dashboard.period.month': 'this month',
    'dashboard.period.overall': 'overall',
    'dashboard.toast.refreshSuccess': 'Dashboard updated successfully.',
    'dashboard.refresh': 'Refresh',
    'dashboard.refreshing': 'Refreshing...',
    'dashboard.loading': 'Loading data…',
    'dashboard.loadFailed': "Couldn't load the dashboard figures.",
    'dashboard.retry': 'Try again',
    'dashboard.stat.cancelledOrNoShow': 'Cancelled or no-show',
    'dashboard.metric.totalPaymentRevenue': 'Total Payment Revenue',
    'dashboard.stat.totalAppointments': 'Total Appointments',
    'dashboard.stat.employees': 'Employees',
    'dashboard.stat.activeBranches': 'Active branches',
    'dashboard.chart.bookings': 'Bookings',
    'dashboard.chart.amount': 'Amount',
    'dashboard.chart.totalRevenue': 'Total revenue',
    'dashboard.chart.reservationRate': 'Reservation rate',
    'dashboard.dateRange.allTime': 'All-time',
    'dashboard.dateRange.today': 'Today',
    'dashboard.dateRange.last7days': 'Last 7 days',
    'dashboard.dateRange.last30days': 'Last 30 days',
    'dashboard.dateRange.date': 'Date',
    'dashboard.dateRange.ariaLabel': 'Date range filter',
    // Owner Payments page (AB#2060 — pay-at-salon content cleanup). V1 shows
    // only the pay-at-salon Transactions view; the Commissions/Payouts tabs
    // and Stripe wording stay hidden behind the online-payments flag.
    'payments.title': 'Payments',
    'payments.subtitle.payAtSalon': 'Payments collected at your salon',
    'payments.subtitle.online': 'Manage your transactions, commissions, and payouts',
    'payments.tab.transactions': 'Transactions',
    'payments.tab.commissions': 'Commissions',
    'payments.tab.payouts': 'Payouts',
    'payments.retry': 'Retry',
    'payments.summary.totalRevenue': 'Total Revenue',
    'payments.summary.transactions': 'Transactions',
    'payments.filter.status': 'Payment Status',
    'payments.filter.from': 'From',
    'payments.filter.to': 'To',
    'payments.filter.clear': 'Clear Filters',
    'payments.col.date': 'Date',
    'payments.col.appointment': 'Appointment',
    'payments.col.stripePi': 'Stripe PI',
    'payments.col.amount': 'Amount',
    'payments.col.status': 'Status',
    'payments.col.method': 'Method',
    'payments.empty.title': 'No payments yet',
    'payments.empty.body': 'Payments you record at your salon will appear here.',
    // Shared Calendar component (used by 8 pages) — same two strings render
    // identically everywhere; localizing here is the only place they surface.
    'calendar.selectDate': 'Select a date',
    'calendar.all': 'All',
  },
  ar: {
    // Nav / sidebar
    'nav.dashboard': 'لوحة التحكم',
    'nav.appointments': 'المواعيد',
    'nav.services': 'الخدمات',
    'nav.employees': 'الموظفين',
    'nav.promotions': 'العروض',
    'nav.packages': 'الحزم',
    // AB#2034 — "حزمة" (bundle) matches the shipped Packages AR (الحزم/حزمة); NOT "باقة"
    // which is reserved for subscription plans. {name} is bidi-isolated at interpolation.
    'bundle.tag': 'حزمة',
    'bundle.groupAria': 'حزمة: {name}',
    'bundle.partOf': 'ضمن حزمة {name}',
    'packages.title': 'الحزم',
    'packages.sub': 'ازد إيراداتك عبر تجميع الخدمات — بِع عدة خدمات معًا بسعر عرض ليحجز العملاء أكثر في زيارة واحدة.',
    'packages.new': 'حزمة جديدة',
    'packages.loading': 'جارٍ التحميل…',
    'packages.sessions': 'جلسات',
    'packages.valid': 'الصلاحية',
    'packages.empty.h': 'لا توجد حزم بعد',
    'packages.empty.p': 'أنشئ أول حزمة مدفوعة مسبقًا لتبدأ ببيع الخدمات معًا بخصم.',
    'packages.gate.h': 'الحزم متوفّرة في باقتَي المحترفة والمؤسسات',
    'packages.gate.p': 'تتيح لك الحزم تجميع الخدمات بسعر عرض ليحجز العملاء أكثر في زيارة واحدة. قم بالترقية إلى باقة المحترفة أو المؤسسات لإنشاء الحزم وبيعها.',
    'packages.gate.cta': 'الترقية إلى باقة المحترفة',
    'packages.newTitle': 'حزمة جديدة',
    'packages.editTitle': 'تعديل الحزمة',
    'packages.field.name': 'اسم الحزمة',
    'packages.field.services': 'الخدمات المضمّنة',
    'packages.col.service': 'الخدمة',
    'packages.col.qty': 'الكمية',
    'packages.col.unit': 'سعر الوحدة',
    'packages.noServices': 'لا توجد خدمات بعد. أضف واحدة أدناه.',
    'packages.emptyServices': 'أضف خدمة من الأعلى لإنشاء الحزمة.',
    'packages.searchService': 'ابحث عن خدمة…',
    'packages.addService': 'إضافة خدمة…',
    'packages.add': 'إضافة',
    'packages.qtyMinus': 'إنقاص الكمية',
    'packages.qtyPlus': 'زيادة الكمية',
    'packages.removeService': 'إزالة الخدمة',
    'packages.field.regular': 'السعر الأصلي (تلقائي)',
    'packages.field.package': 'سعر الحزمة',
    'packages.field.savings': 'التوفير',
    'packages.field.duration': 'صالحة لمدة',
    'packages.unit.days': 'أيام',
    'packages.unit.weeks': 'أسابيع',
    'packages.unit.months': 'أشهر',
    'packages.field.showin': 'إظهار في',
    'packages.mobile': 'تطبيق الجوال',
    'packages.online': 'صفحة الحجز الإلكتروني',
    'packages.advanced': 'خيارات متقدمة',
    'packages.transferable': 'السماح بالتحويل إلى عميل آخر',
    'packages.maxPurchases': 'الحد الأقصى للمشتريات',
    'packages.unlimited': 'غير محدود',
    'packages.save': 'حفظ الحزمة',
    'packages.cancel': 'إلغاء',
    'packages.toast.created': 'تم إنشاء الحزمة',
    'packages.toast.updated': 'تم تحديث الحزمة',
    'packages.toast.deleted': 'تمت إزالة الحزمة',
    'packages.toast.saveFailed': 'تعذّر حفظ الحزمة',
    'packages.toast.deleteFailed': 'تعذّر إزالة الحزمة',
    'packages.err.name': 'أدخل اسم الحزمة',
    'packages.err.items': 'أضف خدمة واحدة على الأقل',
    'packages.err.price': 'يجب أن يكون سعر الحزمة أقل من السعر الأصلي',
    'packages.servicesInPackage': 'الخدمات في هذه الحزمة',
    'packages.itemsCount': 'خدمات',
    'packages.branches': 'متوفّرة في الفروع',
    'packages.allBranches': 'متوفّرة في كل الفروع — اختر فروعًا لتحديدها.',
    'packages.branchesHint': 'متوفّرة في الفروع المحددة فقط.',
    'packages.branchesAll': 'كل الفروع',
    'packages.branchesSpecific': 'فروع محددة',
    'packages.pickBranchHint': 'اختر فرعًا واحدًا على الأقل.',
    'packages.discount': 'الخصم',
    'packages.tab.active': 'النشطة',
    'packages.tab.archived': 'المؤرشفة',
    'packages.emptyArchived.h': 'لا توجد حزم مؤرشفة',
    'packages.status.active': 'نشطة',
    'packages.status.scheduled': 'مجدولة',
    'packages.status.paused': 'متوقّفة',
    'packages.status.ended': 'منتهية',
    'packages.status.archived': 'مؤرشفة',
    'packages.pause': 'إيقاف مؤقت',
    'packages.resume': 'استئناف',
    'packages.edit': 'تعديل',
    'packages.archive': 'أرشفة',
    'packages.restore': 'استعادة',
    'packages.toast.archived': 'تمت أرشفة الحزمة',
    'packages.toast.restored': 'تمت استعادة الحزمة — متوقّفة',
    'packages.salesEndsAt': 'إيقاف البيع بتاريخ',
    'packages.salesEndsAtHint': 'اختياري — اتركه فارغًا للبيع دون تاريخ انتهاء.',
    'packages.salesEndsAtSet': 'سيتوقّف البيع بعد هذا التاريخ.',
    'packages.customerSaves': 'يوفّر العميل',
    'packages.moreOptions': 'الظهور وخيارات متقدمة',
    'packages.noMatches': 'لا توجد خدمات مطابقة',
    'packages.addServiceHint': 'أضف خدمة… ابحث بالاسم',
    'nav.customers': 'العملاء',
    'nav.payments': 'المدفوعات',
    'nav.subscription': 'الاشتراك',
    'nav.loyalty': 'الولاء',
    'nav.inventory': 'المخزون',
    'nav.referrals': 'الإحالات',
    'nav.settings': 'الإعدادات',
    'nav.profile': 'الملف الشخصي',
    'nav.users': 'المستخدمين',
    'nav.owners': 'المالكين',
    'nav.featureFlags': 'خصائص التحكم',
    'nav.auditLog': 'سجل التدقيق',
    'nav.apiKeys': 'مفاتيح API',
    'nav.sponsoredPlacements': 'الترشيحات المدفوعة',
    'nav.platformPromotions': 'عروض المنصّة',
    'nav.categories': 'الفئات',
    // AB#2066 P1-A (scoped owner-portal Arabic closure) — committee-signed 2026-09-12.
    'nav.calendar': 'التقويم',
    'nav.myBusiness': 'منشأتي',
    'settings.title': 'الإعدادات',
    'settings.section.account': 'إدارة الحساب',
    'settings.card.accountSettings.title': 'إعدادات الحساب',
    'settings.card.accountSettings.desc': 'إدارة إعدادات الحساب ومعلومات الدفع',
    'settings.card.profile.title': 'الملف الشخصي',
    'settings.card.profile.desc': 'عرض ملفك الشخصي وتعديله',
    'settings.card.payment.title': 'إعدادات الدفع',
    'settings.card.payment.desc': 'إدارة طريقة الدفع',
    'settings.card.branches.title': 'الفروع',
    'settings.card.branches.desc': 'إدارة عدة فروع',
    'settings.section.notifications': 'الإشعارات',
    'settings.card.notifications.title': 'تفضيلات الإشعارات',
    'settings.card.notifications.desc': 'إدارة إعدادات إشعارات البريد الإلكتروني والتطبيق والإشعارات الفورية',
    'mybiz.group.yourBusiness': 'منشأتك',
    'mybiz.section.businessDetails': 'تفاصيل المنشأة',
    'mybiz.section.branches': 'الفروع',
    'mybiz.section.categories': 'الفئات',
    'mybiz.section.services': 'الخدمات',
    'mybiz.section.calendarSettings': 'إعدادات التقويم',
    'mybiz.group.team': 'الفريق',
    'mybiz.section.manageStaff': 'إدارة الموظفين',
    'mybiz.aria.sections': 'أقسام منشأتي',
    // AB#2108 (Admin V1 P0-2) — create business / Submit to Devanza / approval banner. arabic-translator sign-off 2026-09-21 (AB#2108 Localization section).
    'mybiz.create.title': 'أنشئ منشأتك',
    'mybiz.create.body': 'ابدأ باسم صالونك ومدينته. بعد ذلك أضف التفاصيل وفرعًا وساعات العمل والخدمات، ثم أرسل صالونك إلى Devanza للمراجعة.',
    'mybiz.create.name': 'اسم المنشأة',
    'mybiz.create.country': 'الدولة',
    'mybiz.create.city': 'المدينة',
    'mybiz.create.country.AE': 'الإمارات العربية المتحدة',
    'mybiz.create.country.JO': 'الأردن',
    'mybiz.create.action': 'إنشاء المنشأة',
    'mybiz.create.saving': 'جارٍ الإنشاء…',
    'mybiz.create.error.name': 'أدخل اسم منشأتك (من 3 إلى 100 حرف).',
    'mybiz.create.error.city': 'أدخل مدينتك (من 2 إلى 100 حرف).',
    'mybiz.create.success': 'تم إنشاء المنشأة. أكمل الأقسام، ثم أرسلها إلى Devanza.',
    'mybiz.create.failed': 'تعذّر إنشاء منشأتك. حاول مرة أخرى.',
    'mybiz.create.sessionFailed': 'تم إنشاء منشأتك. سجّل الخروج ثم سجّل الدخول مرة أخرى للمتابعة.',
    'mybiz.submit.title': 'إرسال صالونك إلى Devanza',
    'mybiz.submit.body': 'عندما يصبح صالونك جاهزًا، أرسله للمراجعة. تراجع Devanza كل صالون قبل نشره، وستُعلمك عند اعتماده.',
    'mybiz.submit.item.businessDetails': 'تفاصيل المنشأة وساعات العمل',
    'mybiz.submit.item.branches': 'فرع نشط واحد على الأقل مع موقعه على الخريطة',
    'mybiz.submit.item.categories': 'فئة واحدة على الأقل',
    'mybiz.submit.item.services': 'خدمة نشطة واحدة على الأقل',
    'mybiz.submit.item.staff': 'موظف نشط واحد على الأقل',
    'mybiz.submit.staffMissing': 'أضف موظفًا نشطًا واحدًا على الأقل يقدّم خدمة في فرع نشط، ثم أعد الإرسال.',
    'mybiz.submit.itemDone': 'مكتمل',
    'mybiz.submit.itemTodo': 'غير مكتمل',
    'mybiz.submit.itemChecking': 'جارٍ التحقق',
    'mybiz.submit.itemUnknown': 'لم يتم التحقق',
    'mybiz.submit.pinHint': 'يعثر العملاء على صالونك من خلال موقع فرعه، لذا حدّد موقع كل فرع على الخريطة.',
    'mybiz.submit.consent': 'أؤكد أن هذه البيانات صحيحة، وأوافق على الشروط والأحكام وسياسة الخصوصية الخاصة بـ Devanza.',
    'mybiz.submit.termsLink': 'الشروط والأحكام',
    'mybiz.submit.privacyLink': 'سياسة الخصوصية',
    'mybiz.submit.action': 'إرسال إلى Devanza',
    'mybiz.submit.saving': 'جارٍ الإرسال…',
    'mybiz.submit.incomplete': 'أكمل العناصر أعلاه لتتمكن من الإرسال.',
    'mybiz.submit.checking': 'جارٍ التحقق من إعداد صالونك…',
    'mybiz.submit.checkFailed': 'تعذّر التحقق من إعداد صالونك.',
    'mybiz.submit.retry': 'حاول مرة أخرى',
    'mybiz.submit.success': 'تم الإرسال. ستراجع Devanza صالونك وتُعلمك بالنتيجة.',
    'mybiz.submit.failed': 'تعذّر إرسال صالونك. راجع البيانات وحاول مرة أخرى.',
    'approval.banner.pending': 'صالونك بانتظار الموافقة. تراجعه Devanza الآن وستُعلمك عند نشره.',
    'approval.banner.rejected': 'تحتاج Devanza إلى بعض التعديلات قبل نشر صالونك.',
    'approval.banner.reason': 'السبب:',
    'approval.banner.update': 'حدّث البيانات وأعد الإرسال',
    'payments.status.all': 'جميع الحالات',
    'payments.status.completed': 'مكتمل',
    'payments.status.pending': 'قيد الانتظار',
    'payments.status.failed': 'فشل',
    'payments.status.refunded': 'مُسترد',
    'nav.chat': 'المحادثة',
    'nav.help': 'المساعدة',
    'nav.support': 'الدعم',
    // AB#1818 — Super Admin Categories page (gender-neutral verbal nouns)
    'superadmin.categories.title': 'الفئات',
    'superadmin.categories.subtitle': 'إدارة فئات السوق المعروضة على تطبيق العملاء',
    'superadmin.categories.new': 'فئة جديدة',
    'superadmin.categories.tab.all': 'الكل',
    'superadmin.categories.tab.pending': 'بانتظار الموافقة',
    'superadmin.categories.tab.inactive': 'غير مفعّلة',
    'superadmin.categories.status.approved': 'معتمدة',
    'superadmin.categories.status.pending': 'قيد الانتظار',
    'superadmin.categories.status.rejected': 'مرفوضة',
    'superadmin.categories.status.active': 'مفعّلة',
    'superadmin.categories.status.inactive': 'غير مفعّلة',
    'superadmin.categories.column.image': 'الصورة',
    'superadmin.categories.column.name': 'الاسم',
    'superadmin.categories.column.nameAr': 'الاسم (عربي)',
    'superadmin.categories.column.status': 'الحالة',
    'superadmin.categories.column.actions': 'الإجراءات',
    'superadmin.categories.action.approve': 'اعتماد',
    'superadmin.categories.action.reject': 'رفض',
    'superadmin.categories.action.edit': 'تعديل',
    'superadmin.categories.action.deactivate': 'إلغاء التفعيل',
    'superadmin.categories.action.cancel': 'إلغاء',
    'superadmin.categories.create.title': 'فئة جديدة',
    'superadmin.categories.create.name': 'الاسم (إنجليزي)',
    'superadmin.categories.create.nameAr': 'الاسم (عربي)',
    'superadmin.categories.create.image': 'الصورة',
    'superadmin.categories.create.image.drop': 'أفلت الصورة هنا أو انقر للاختيار',
    'superadmin.categories.create.image.hint': 'JPG, PNG, WebP · حد أقصى 2 ميغابايت · يُفضّل 264×264 بكسل',
    'superadmin.categories.create.image.remove': 'إزالة الصورة',
    'superadmin.categories.create.activeNow': 'مفعّلة فوراً',
    'superadmin.categories.create.autoApprovedNotice': 'فعّل "مفعّلة فوراً" للنشر الآن، أو اتركها معطّلة لإبقاء الفئة مخفية.',
    'superadmin.categories.create.submit': 'إنشاء',
    'superadmin.categories.reject.title': 'رفض الفئة',
    'superadmin.categories.reject.reason': 'السبب',
    'superadmin.categories.reject.reason.hint': 'لا يقل عن 10 أحرف. يظهر لصاحب الصالون.',
    'superadmin.categories.reject.reason.counter': '{count}/10 كحد أدنى',
    'superadmin.categories.reject.submit': 'رفض',
    'superadmin.categories.deactivate.title': 'إلغاء تفعيل الفئة',
    'superadmin.categories.deactivate.body': 'إلغاء التفعيل يُخفي هذه الفئة من التصفّح. تبقى الخدمات قابلة للحجز ويتم الإبقاء على المواعيد الحالية. يمكنك إعادة تفعيلها لاحقًا.',
    'superadmin.categories.deactivate.confirm': 'إلغاء التفعيل',
    'superadmin.categories.deactivate.submit': 'إلغاء التفعيل',
    'superadmin.categories.empty.all': 'لا توجد فئات بعد. أنشئ الأولى.',
    'superadmin.categories.empty.deactivated': 'لا توجد فئات غير مفعّلة. الفئات المفعّلة تظهر ضمن «الكل».',
    'superadmin.categories.toast.created': 'تم إنشاء الفئة',
    'superadmin.categories.toast.approved': 'تمت الموافقة على الفئة',
    'superadmin.categories.toast.rejected': 'تم رفض الفئة',
    'superadmin.categories.toast.deactivated': 'تم إلغاء تفعيل الفئة',
    'superadmin.categories.toast.error': 'حدث خطأ ما — يرجى المحاولة مرة أخرى.',
    'superadmin.categories.error.duplicate': 'توجد فئة بهذا الاسم بالفعل.',
    'superadmin.categories.error.imageTooLarge': 'حجم الصورة يتجاوز 2 ميغابايت — اختر صورة أصغر أو اضغطها قبل الرفع.',
    'superadmin.categories.error.imageDimensions': 'أبعاد الصورة صغيرة جداً — ارفع صورة لا تقل عن 200×200 بكسل.',
    'superadmin.categories.error.imageType': 'اختر صورة بصيغة JPG أو PNG أو WebP.',
    'superadmin.categories.error.fetch': 'تعذّر تحميل الفئات.',
    'superadmin.categories.error.nameRequired': 'الاسم مطلوب (2-60 حرفاً).',
    'superadmin.categories.error.nameArRequired': 'الاسم بالعربي مطلوب (2-120 حرفاً).',
    'superadmin.categories.error.imageRequired': 'الصورة مطلوبة.',
    'superadmin.categories.error.reasonRequired': 'يرجى إدخال 10 أحرف على الأقل.',
    'superadmin.categories.retry': 'إعادة المحاولة',
    // AB#1872 — دورة حياة الفئة (التعديل / إلغاء التفعيل / إعادة التفعيل) — صيغة محايدة
    'superadmin.categories.tab.deactivated': 'غير مفعّلة',
    'superadmin.categories.status.deactivated': 'غير مفعّلة',
    'superadmin.categories.action.reactivate': 'إعادة التفعيل',
    'superadmin.categories.action.more': 'إجراءات أخرى',
    'superadmin.categories.edit.title': 'تعديل الفئة',
    'superadmin.categories.edit.submit': 'حفظ التغييرات',
    'superadmin.categories.edit.renameReach': 'تغيير اسم هذه الفئة يُحدّثه لدى {count} من الصالونات المشتركة.',
    'superadmin.categories.edit.image.replace': 'أفلت صورة جديدة هنا أو انقر للاستبدال',
    'superadmin.categories.edit.image.current': 'الصورة الحالية',
    'superadmin.categories.toast.updated': 'تم تحديث الفئة',
    'superadmin.categories.toast.deactivated.done': 'تم إلغاء تفعيل الفئة',
    'superadmin.categories.toast.reactivated': 'تمت إعادة تفعيل الفئة',
    // AB#1870 — التخصيص حسب الجنس + شارة الصف
    'superadmin.categories.gender.label': 'التخصيص حسب الجنس',
    'superadmin.categories.gender.none': 'بدون تفضيل',
    'superadmin.categories.gender.female': 'إناث',
    'superadmin.categories.gender.male': 'ذكور',
    'superadmin.categories.gender.hint': 'معظم الفئات تخدم الجميع. حدِّد التخصيص فقط عند ارتباط الفئة بجنس واحد.',
    'categories.target.female': 'للإناث',
    'categories.target.male': 'للذكور',
    // AB#1871 — أعداد تأثير إلغاء التفعيل + التوسيع الكسول (أكورديون)
    'superadmin.categories.impact.salons': 'الصالونات المشتركة',
    'superadmin.categories.impact.services': 'الخدمات',
    'superadmin.categories.impact.bookings': 'الحجوزات القادمة',
    'superadmin.categories.impact.detail.salons': 'عرض الصالونات المتأثرة',
    'superadmin.categories.impact.detail.services': 'عرض الخدمات المتأثرة',
    'superadmin.categories.impact.detail.bookings': 'عرض الحجوزات القادمة',
    'superadmin.categories.impact.onlyCategory': 'الفئة الوحيدة',
    'superadmin.categories.impact.more': '+ {count} أخرى',
    'superadmin.categories.impact.empty': 'لا توجد عناصر',
    'superadmin.categories.impact.loadError': 'تعذّر التحميل. أعد المحاولة.',
    'superadmin.categories.impact.serviceFrom': 'ابتداءً من {price} {currency}',
    'superadmin.categories.deactivate.singleCategoryWarning': '{count} من أصل {total} صالون لا تملك فئة أخرى وستختفي من تصفّح الفئات إلى حين إعادة تصنيفها.',
    'superadmin.categories.deactivate.impactError': 'تعذّر تحميل ملخّص التأثير. حاول مرة أخرى قبل إلغاء التفعيل.',
    // ── Epic AB#1824 — Business Hours Matrix (Model E) — Dina copy table ──
    // Authored by Dina; arabic-translator sign-off pending — these are MSA
    // draft strings that match rows 1-50 of the design handoff.
    'hoursMatrix.tab.label': 'ساعات العمل',
    'hoursMatrix.title': 'ساعات عمل النشاط',
    'hoursMatrix.subtitle.single': '{salonName} · فرع واحد',
    'hoursMatrix.subtitle.uniform': '{salonName} · {n} فروع · الساعات موحّدة',
    'hoursMatrix.subtitle.divergent': '{salonName} · {n} فروع · {k} يختلف عن الافتراضي',
    'hoursMatrix.defaultRow.label': 'الافتراضي',
    'hoursMatrix.defaultRow.sub.single': 'يطبَّق على جميع الفروع',
    'hoursMatrix.defaultRow.sub.uniform': 'التعديلات تطبَّق على {n} فروع',
    'hoursMatrix.defaultRow.sub.divergent.one': '⚠ خلية واحدة تختلف',
    'hoursMatrix.defaultRow.sub.divergent.other': '⚠ {k} خلايا تختلف',
    'hoursMatrix.defaultRow.pill.single': 'فرع واحد',
    'hoursMatrix.defaultRow.pill.uniform': 'جميع الفروع',
    'hoursMatrix.defaultRow.pill.divergent': 'الفعلي',
    'hoursMatrix.cell.open': '{openTime} – {closeTime}',
    'hoursMatrix.cell.closed': 'مُغلق',
    'hoursMatrix.cell.matchesDefault': '(مطابق للافتراضي)',
    'hoursMatrix.cell.differsFromDefault': 'يختلف عن الافتراضي',
    'hoursMatrix.cell.unsaved': 'غير محفوظ',
    'hoursMatrix.cell.differsCount.one': 'يختلف 1',
    'hoursMatrix.cell.differsCount.other': 'يختلف {n}',
    'hoursMatrix.cell.stale': 'قديم',
    'hoursMatrix.editor.open': 'الفتح',
    'hoursMatrix.editor.close': 'الإغلاق',
    'hoursMatrix.editor.closedAllDay': 'مُغلق طوال اليوم',
    'hoursMatrix.editor.save': 'حفظ',
    'hoursMatrix.editor.cancel': 'إلغاء',
    'hoursMatrix.editor.openHour': 'ساعة الفتح',
    'hoursMatrix.editor.openMinute': 'دقيقة الفتح',
    'hoursMatrix.editor.closeHour': 'ساعة الإغلاق',
    'hoursMatrix.editor.closeMinute': 'دقيقة الإغلاق',
    // T1 (translator review 2026-05-31): Arabic doesn't use 3-letter weekday
    // truncations natively; native conventions are either single-letter or
    // full names. Dina's mockup §J uses full names in the RTL column headers
    // (السبت, الجمعة...) and the matrix grid `minmax(110px, 1fr)` accommodates
    // them comfortably. Switching to full names matches the mockup + reads
    // naturally for native Arabic users.
    // arabic-translator §7.2 sign-off (2026-05-31): Revised `الإثنين` → `الاثنين`
    // (Mon, short + long) to match the canonical modern-MSA form locked in the
    // mobile §7.1 sign-off (`booking.day.monday`). Keeps day-name spelling
    // consistent across web + mobile surfaces for any string that interpolates
    // a day token (matrix subtitle, BulkApplyConfirmModal title, toast, banner,
    // staffConflict body, rejection.branchClosed.bannerTitle, etc).
    'hoursMatrix.day.sun.short': 'الأحد',
    'hoursMatrix.day.mon.short': 'الاثنين',
    'hoursMatrix.day.tue.short': 'الثلاثاء',
    'hoursMatrix.day.wed.short': 'الأربعاء',
    'hoursMatrix.day.thu.short': 'الخميس',
    'hoursMatrix.day.fri.short': 'الجمعة',
    'hoursMatrix.day.sat.short': 'السبت',
    'hoursMatrix.day.sun.long': 'الأحد',
    'hoursMatrix.day.mon.long': 'الاثنين',
    'hoursMatrix.day.tue.long': 'الثلاثاء',
    'hoursMatrix.day.wed.long': 'الأربعاء',
    'hoursMatrix.day.thu.long': 'الخميس',
    'hoursMatrix.day.fri.long': 'الجمعة',
    'hoursMatrix.day.sat.long': 'السبت',
    'hoursMatrix.column.branch': 'الفرع',
    'hoursMatrix.branch.revertLink': '↻ إعادة إلى الافتراضي',
    'hoursMatrix.branch.revertAllLink': '↻ إعادة الكل إلى الافتراضي',
    'hoursMatrix.branch.hq': 'المقر الرئيسي',
    'hoursMatrix.branch.cellRevertTip': '↻ إعادة',
    'hoursMatrix.branch.lastDivergenceNote': 'جميع الفروع متطابقة الآن — سيصبح الصف الافتراضي المحرر الوحيد بعد التحديث',
    'hoursMatrix.revertConfirm.text': 'إعادة {branch} · {day} إلى {newValue} (مطابقاً للافتراضي)؟',
    'hoursMatrix.revertConfirm.apply': 'إعادة',
    'hoursMatrix.revertConfirm.cancel': 'إلغاء',
    'hoursMatrix.bulkModal.title.singleDay': 'تطبيق ساعات {day} على جميع الفروع الـ{n}؟',
    'hoursMatrix.bulkModal.title.multiDay': 'تطبيق التغييرات على جميع الفروع الـ{n}؟',
    'hoursMatrix.bulkModal.subhead': 'اختر الفروع التي تريد تحديثها.',
    'hoursMatrix.bulkModal.diff.deselected': 'تم إلغاء التحديد — ستبقى الساعات كما هي',
    'hoursMatrix.bulkModal.summary': 'سيتم تحديث {k} من أصل {n} فرع',
    'hoursMatrix.bulkModal.cancel': 'إلغاء',
    // T4 (translator review): Arabic plural agreement for "Apply to {k} branches"
    // needs distinct shapes for k=1 (singular) vs k>=2 (plural). Adding .one /
    // .other variants so the pluralize helper renders correct grammar.
    'hoursMatrix.bulkModal.confirm': 'تطبيق على {k} فروع',
    'hoursMatrix.bulkModal.confirm.one': 'تطبيق على فرع واحد',
    'hoursMatrix.bulkModal.confirm.other': 'تطبيق على {k} فروع',
    'hoursMatrix.bulkModal.deselectAll': 'إلغاء التحديد للكل',
    'hoursMatrix.bulkModal.headsUp': 'تنبيه: ستُستبدل التجاوزات الخاصة بكل فرع في الفروع المحددة.',
    'hoursMatrix.bulkModal.confirmLabel': 'تأكيد التطبيق الشامل',
    'hoursMatrix.banner.concurrency.title': 'شخص آخر قام بتحديث هذه الساعات',
    'hoursMatrix.banner.concurrency.body': 'قام {editorName} بتحديث ساعات {branchScope} منذ {timeAgo}. لم يتم حفظ مسودّتك. راجع الإصدار الأخير وحاول مرة أخرى.',
    'hoursMatrix.banner.concurrency.bodyGeneric': 'قام مستخدم آخر بتحديث هذه الساعات. لم يتم حفظ مسودّتك. راجع الإصدار الأخير وحاول مرة أخرى.',
    'hoursMatrix.banner.concurrency.review': 'مراجعة التغييرات',
    'hoursMatrix.banner.concurrency.discard': 'تجاهل مسودّتي',
    'hoursMatrix.banner.concurrency.dismiss': 'إغلاق',
    'hoursMatrix.banner.concurrency.scope.branch': '{branch}',
    'hoursMatrix.banner.concurrency.scope.multiple': '{count} فروع',
    // T5 (translator review): correct singular agreement for k=1.
    'hoursMatrix.banner.concurrency.scope.multiple.one': 'فرع واحد',
    'hoursMatrix.banner.concurrency.scope.multiple.other': '{count} فروع',
    'hoursMatrix.staffConflict.title': 'لن تكون نوبات {day} قابلة للحجز',
    'hoursMatrix.staffConflict.body.matrix': '⚠️ لن تظهر نوبات {day} في الجدول المتاح لأن {branch} مُغلق يوم {day}. حدّث ساعات الفرع للتمكين.',
    'hoursMatrix.staffConflict.body.modal': '⚠️ يتأثر {n} من جداول الموظفين ({sampleNames}). سيتم الحفظ، لكن لن تُحجز النوبات في هذا اليوم.',
    'hoursMatrix.staffConflict.viewStaffCta': 'عرض جداول الموظفين',
    'hoursMatrix.branchCard.readonlyHeader': 'الساعات الفعلية',
    'hoursMatrix.branchCard.readonlyHeader.matchesDefault': '· مطابقة للافتراضي',
    'hoursMatrix.branchCard.readonlyHeader.differs.one': '· يوم واحد يختلف عن الافتراضي',
    'hoursMatrix.branchCard.readonlyHeader.differs.other': '· {n} أيام تختلف عن الافتراضي',
    'hoursMatrix.branchCard.editLink': 'تحرير في ساعات العمل ←',
    'hoursMatrix.branchCard.explainer': 'تُدار الساعات الآن من تفاصيل النشاط. بطاقات الفروع هي ملخصات للقراءة فقط — حرّر مرة واحدة في المصفوفة وتسري التغييرات حيث تختار.',
    'hoursMatrix.branchCard.divergesNote': '· يختلف عن الافتراضي ({defaultValue})',
    'hoursMatrix.toast.success.singleBranch': 'تم تحديث ساعات {day} في {branch}',
    'hoursMatrix.toast.success.bulk': 'تم تحديث ساعات {day} على {k} فروع',
    // T6 (translator review): singular agreement for the 1-branch case.
    'hoursMatrix.toast.success.bulk.one': 'تم تحديث ساعات {day} على فرع واحد',
    'hoursMatrix.toast.success.bulk.other': 'تم تحديث ساعات {day} على {k} فروع',
    'hoursMatrix.toast.success.bulkGeneric': 'تم تحديث الساعات على {k} فروع',
    'hoursMatrix.toast.success.bulkGeneric.one': 'تم تحديث الساعات على فرع واحد',
    'hoursMatrix.toast.success.bulkGeneric.other': 'تم تحديث الساعات على {k} فروع',
    'hoursMatrix.toast.revertSuccess': 'تمت إعادة {day} في {branch} إلى الافتراضي',
    'hoursMatrix.toast.postSaveShiftsWarning.one': 'تم الحفظ. لن يكون بالإمكان حجز نوبة {day} واحدة في {branch}.',
    'hoursMatrix.toast.postSaveShiftsWarning.other': 'تم الحفظ. لن يكون بالإمكان حجز {n} نوبة في {day} بفرع {branch}.',
    'hoursMatrix.toast.error.generic': 'تعذّر حفظ الساعات. يرجى المحاولة مرة أخرى.',
    'hoursMatrix.toast.error.staleHours': 'قام مستخدم آخر بتحديث الساعات. تم التحديث — يرجى المراجعة والحفظ مرة أخرى.',
    'hoursMatrix.toast.error.branchNotFound': 'تم حذف فرع أثناء التحرير. تم التحديث إلى أحدث إصدار.',
    'hoursMatrix.toast.error.validation': 'بعض الخلايا تحتوي على أوقات غير صالحة. يرجى تصحيحها والمحاولة مرة أخرى.',
    'hoursMatrix.emptyState.noBranches': 'أضف فرعك الأول من تبويب الفروع لبدء ضبط الساعات.',
    'hoursMatrix.loading': 'جارٍ تحميل الساعات…',
    'hoursMatrix.error.fetch': 'تعذّر تحميل ساعات العمل.',
    'hoursMatrix.error.retry': 'إعادة المحاولة',
    'hoursMatrix.actions.cancel': 'إلغاء',
    'hoursMatrix.actions.save': 'حفظ',
    'hoursMatrix.actions.saving': 'جارٍ الحفظ…',
    'hoursMatrix.foot.singleHint': 'اضغط على أي يوم للتحرير · التغييرات تطبَّق فقط على {salonName}',
    'hoursMatrix.foot.uniformHint': 'تحرير الافتراضي سيطلب التأكيد للتطبيق على جميع الفروع الـ{n}',
    'hoursMatrix.foot.divergentHint': 'اضغط على أي خلية للتحرير · التعديلات لكل فرع لا تُطلق نافذة التطبيق الشامل',
    'hoursMatrix.foot.lastDivergenceHint': 'إذا كانت هذه آخر اختلافة، سيعود المصفوف إلى وضع التطابق بعد التحديث',
    'hoursMatrix.foot.staleHint': 'الحفظ معطّل أثناء وجود تعديلات متزامنة',
    'hoursMatrix.divergence.chip.one': 'يومٌ واحد يختلف عن الافتراضي',
    // AB#1824 polish — translator §7.2 minor concern #3: feminine plural
    // agreement with خلية / خلايا (cells). `تختلف` (feminine) agrees with
    // the implied feminine plural subject; previous `يختلف` (masculine
    // singular) read awkwardly in context.
    'hoursMatrix.divergence.chip.other': '{k} تختلف عن الافتراضي',
    'hoursMatrix.uniform.chip': 'الكل متطابق',
    'hoursMatrix.legend.open': 'مفتوح',
    'hoursMatrix.legend.closed': 'مُغلق',
    'hoursMatrix.legend.divergent': 'يختلف عن الافتراضي',
    'hoursMatrix.legend.stale': 'خلية قديمة — مسودّتك تختلف عن أحدث إصدار من الخادم',
    'hoursMatrix.a11y.grid': 'محرر ساعات العمل، {n} فروع، استخدم مفاتيح الأسهم للتنقل بين الخلايا',
    'hoursMatrix.a11y.defaultRow': 'صف الساعات الافتراضي. ينطبق على جميع الفروع عند التطابق.',
    'hoursMatrix.a11y.branchRow': 'صف ساعات {branch}. {k} يوم/أيام يختلف عن الافتراضي.',
    'hoursMatrix.a11y.cell.divergent': '{day}، {branch}، {open}–{close}، يختلف عن الافتراضي. اضغط Enter للتحرير.',
    'hoursMatrix.a11y.cell.open': '{day}، {branch}، من {open} إلى {close}. اضغط Enter للتحرير.',
    'hoursMatrix.a11y.cell.closed': '{day}، {branch}، مُغلق. اضغط Enter للتحرير.',
    'hoursMatrix.a11y.cell.matchesDefault': '{day}، {branch}، مطابق للافتراضي. اضغط Enter للتحرير.',
    'hoursMatrix.a11y.banner': 'تنبيه تعارض تعديلات متزامنة',
    'hoursMatrix.a11y.bulkModal': 'تأكيد التطبيق الشامل',
    'hoursMatrix.a11y.readonlyTable': 'الساعات الفعلية لفرع {branch} · للقراءة فقط',
    'hoursMatrix.branchesStrip.label.one': 'فرع واحد متطابق',
    'hoursMatrix.branchesStrip.label.other': '{n} فروع متطابقة',
    'hoursMatrix.branchesStrip.expandHint': 'اضغط لعرض كل فرع على حدة',
    'hoursMatrix.branchesStrip.collapseHint': 'اضغط للعودة إلى عرض الافتراضي فقط',
    'rejection.branchClosed.toast': 'الفرع مُغلق في هذا اليوم.',
    'rejection.branchClosed.bannerTitle': '{branch} مُغلق يوم {day}.',
    'rejection.branchClosed.bannerBody': 'اختر تاريخاً آخر. لتغيير ساعات العمل، انتقل إلى تفاصيل النشاط ← الساعات.',
    // Header / menu
    'menu.accountSettings': 'إعدادات الحساب',
    'menu.languages': 'اللغات',
    'menu.theme': 'المظهر',
    'menu.themeLight': 'فاتح',
    'menu.themeDark': 'داكن',
    'menu.termsAndConditions': 'الشروط والأحكام',
    'menu.logout': 'تسجيل الخروج',
    'header.search': 'بحث',
    // Toasts
    'toast.languageSet': 'Language set to English',
    'toast.languageSetAr': 'تم تغيير اللغة إلى العربية',
    'toast.themeSetLight': 'تم التبديل إلى المظهر الفاتح',
    'toast.themeSetDark': 'تم التبديل إلى المظهر الداكن',
    // AB#1840 (PBI-B) / AB#1838 — Customer-side gender matrix vocabulary.
    // Wiki §3 canonical AR labels. "Mixed" remains "للجميع" (gender axis);
    // AB#1847 (PBI-I) AgeGroup.Both uses "كلاهما" elsewhere to avoid
    // overload (Q1 decision 2026-05-31).
    'services.gender.menOnly': 'رجالي',
    'services.gender.womenOnly': 'نسائي',
    'services.gender.mixed': 'للجميع',
    'services.gender.aria.menOnly': 'للرجال فقط',
    'services.gender.aria.womenOnly': 'للسيدات فقط',
    'services.gender.aria.mixed': 'للجميع',
    // AB#1847 (PBI-I) / AB#1838 — Customer-side age-group matrix vocabulary.
    // Wiki §3 canonical AR labels. `Both` = `كلاهما` per founder Q1
    // (2026-05-31) — NOT `الجميع`, which is reserved for the gender axis
    // `Everyone` label to avoid bilingual overload. Adult is the silent
    // default (no chip on the service card); the form labels are required.
    'services.ageGroup.label': 'لمن هذه الخدمة؟',
    'services.ageGroup.adults': 'بالغين',
    'services.ageGroup.kids': 'أطفال',
    'services.ageGroup.both': 'كلاهما',
    // AB#1899 — AR variant of the Copy-from auto-suffix. نسخة = "copy"
    // as a noun. The noun does NOT pluralize for the counter — only
    // the numeric is appended (نسخة 2, نسخة 3 …). arabic-translator
    // skill signed off on this phrasing — see PR description.
    'services.copyFrom.suffix.first': '(نسخة)',
    'services.copyFrom.suffix.nth': '(نسخة {n})',
    'services.ageGroup.cardKids': 'أطفال',
    'services.ageGroup.cardBoth': 'كلاهما',
    'services.ageGroup.aria.kids': 'للأطفال',
    'services.ageGroup.aria.both': 'لجميع الأعمار',
    'services.ageGroup.required': 'الرجاء اختيار الفئة العمرية',
    // PBI 1962 (AB#1968) — مجموعات خيارات الخدمة (الخيارات والإضافات).
    // النصوص العربية هي المجموعة المعتمدة من لجنة الترجمة (قسم التعريب في PBI
    // 1962) — لا يُعاد ترجمتها. باقي المفاتيح بصيغة محايدة جندريًا.
    'services.builder.title': 'مجموعات الخيارات',
    'services.builder.intro': 'حدّد مجموعات الخيارات التي يختار منها العملاء. مجموعات «الخيار» هي فئات يُختار منها خيار واحد، ومجموعات «الإضافة» هي إضافات اختيارية. أسعار الخيارات مطلقة، وأسعار الإضافات تُضاف فوق الخيار المُختار.',
    'services.builder.variant': 'خيار',
    'services.builder.addon': 'إضافة',
    'services.builder.required': 'مطلوب',
    'services.builder.optional': 'اختياري',
    'services.builder.implicitBadge': 'مُجمَّع تلقائيًا (قديم)',
    'services.builder.addGroup': 'إضافة مجموعة خيارات',
    'services.builder.addOption': 'إضافة خيار',
    'services.builder.groupName': 'اسم المجموعة',
    'services.builder.groupNameAr': 'اسم المجموعة (بالعربية)',
    'services.builder.groupNamePlaceholder': 'مثال: الطول',
    'services.builder.kind': 'النوع',
    'services.builder.selection': 'الاختيار',
    'services.builder.selectionSingle': 'اختيار واحد',
    'services.builder.selectionMultiple': 'اختيار متعدّد',
    'services.builder.minSelect': 'الحدّ الأدنى',
    'services.builder.maxSelect': 'الحدّ الأقصى',
    'services.builder.optionName': 'اسم الخيار',
    'services.builder.optionNameAr': 'اسم الخيار (بالعربية)',
    'services.builder.priceAbsolute': 'السعر',
    'services.builder.priceDelta': 'السعر الإضافي',
    'services.builder.duration': 'المدة',
    'services.builder.durationMinutes': 'دقيقة',
    'services.builder.requiredToggle': 'مطلوب',
    'services.builder.removeGroup': 'حذف المجموعة',
    'services.builder.removeOption': 'حذف الخيار',
    'services.builder.moveUp': 'تحريك للأعلى',
    'services.builder.moveDown': 'تحريك للأسفل',
    'services.builder.groupsCount': '{n} / 8 مجموعات',
    'services.builder.optionsCount': '{n} / 20 خيارًا',
    'services.builder.empty': 'لا توجد مجموعات خيارات بعد. أضِف مجموعة ليتمكّن العملاء من اختيار الخيارات والإضافات.',
    // رسائل التحقّق في الواجهة (مطابقة لمُحقّقات الخادم).
    'services.builder.error.oneVariant': 'يمكن أن تحتوي الخدمة على مجموعة «خيار» واحدة فقط.',
    'services.builder.error.groupNameRequired': 'اسم المجموعة مطلوب.',
    'services.builder.error.optionNameRequired': 'اسم الخيار مطلوب.',
    'services.builder.error.priceNonNegative': 'يجب أن يكون السعر صفرًا أو أكثر.',
    'services.builder.error.durationNonNegative': 'يجب أن تكون المدة صفرًا أو أكثر.',
    'services.builder.error.minMax': 'لا يمكن أن يكون الحدّ الأدنى أكبر من الحدّ الأقصى.',
    'services.builder.error.maxOneOption': 'أضِف خيارًا واحدًا على الأقل إلى هذه المجموعة.',
    'services.builder.error.tooManyGroups': 'يمكن أن تحتوي الخدمة على 8 مجموعات خيارات كحدٍّ أقصى.',
    'services.builder.error.tooManyOptions': 'يمكن أن تحتوي المجموعة على 20 خيارًا كحدٍّ أقصى.',
    'services.builder.error.singleCurrency': 'يجب أن تستخدم جميع الخيارات في الخدمة العملة نفسها.',
    // التحقّق من الخيارات عند الحجز (يُستخدم أيضًا في تطبيق الجوال) — المجموعة المعتمدة.
    'error.option.variantRequired': 'يُرجى اختيار أحد الخيارات.',
    'error.option.selectionInvalid': 'اختيارك غير صالح. يُرجى مراجعة اختياراتك.',
    // PBI 1962 (AB#1962) — أداة اختيار الخيارات عند الحجز اليدوي (بوابة المالك).
    // صياغة محايدة (مصادر / يُرجى) — بانتظار اعتماد لجنة الترجمة.
    'appointments.options.total': 'الإجمالي',
    'appointments.options.chooseAtLeast': 'يُرجى اختيار {n} على الأقل',
    'appointments.options.chooseAtMost': 'يُرجى اختيار {n} كحدّ أقصى',
    'appointments.options.upTo': '{n} كحدّ أقصى',
    'appointments.options.serverTotalNote': 'يُحتسب الإجمالي من الخيارات المحدّدة، ويُؤكَّد الإجمالي النهائي من جهة الخادم.',
    // AB#1845 (PBI-E) / AB#1838 — Staff gender required at invite (AR).
    // Wording matches the BE `STAFF_GENDER_REQUIRED` AR validator message.
    'staff.fields.gender': 'الجنس',
    'staff.fields.gender.option.male': 'ذكر',
    'staff.fields.gender.option.female': 'أنثى',
    'staff.errors.genderRequired': 'جنس الموظف مطلوب.',
    // Batch B (AB#2176) — موظف بدون حساب دخول؛ دعوة البوابة اختيارية.
    'staff.add.subtitleRecord': 'أضف عضوًا إلى فريق العمل ليتمكن العملاء من الحجز لديه. الدخول إلى البوابة اختياري: أضف البريد الإلكتروني ورقم الهاتف لدعوته.',
    'staff.add.submit': 'إضافة موظف',
    'staff.add.submitAndInvite': 'إضافة وإرسال دعوة',
    'staff.contact.emailOptional': 'البريد الإلكتروني (اختياري، للدخول إلى البوابة)',
    'staff.contact.phoneOptional': 'رقم الهاتف (اختياري، للدخول إلى البوابة)',
    'staff.contact.inviteNeedsBoth': 'لإرسال دعوة إلى البوابة، أدخل البريد الإلكتروني ورقم الهاتف معًا.',
    'staff.contact.invalidEmail': 'صيغة البريد الإلكتروني غير صحيحة',
    'staff.edit.inviteHint': 'لا يملك صلاحية الدخول إلى البوابة بعد. أدخل البريد الإلكتروني ورقم الهاتف لدعوته.',
    'staff.status.noPortal': 'بدون دخول إلى البوابة',
    'staff.status.invitePending': 'الدعوة قيد الانتظار',
    'staff.toast.added': 'تمت إضافة الموظف.',
    'staff.toast.invited': 'تم إرسال الدعوة إلى {email}.',
    'staff.toast.inviteFailed': 'تم حفظ الموظف، لكن تعذّر إرسال الدعوة. {reason}',
    'staff.saving': 'جارٍ الحفظ…',
    'staff.banner.completeGender': 'يرجى إكمال جنس هذا الموظف',
    'staff.banner.completeGender.cta': 'تعديل',
    // AB#2005 (follow-up to AB#2004) — EmployeeModal chrome i18n (AR). Simple
    // MSA, gender-neutral per Devanza rule: progressive states use جارٍ + verbal
    // noun; placeholders use اختيار (verbal noun) not اختر (masc. imperative);
    // status نشط/غير نشط (user context per glossary). Reviewed by the
    // arabic-translator committee. Reuses staff.fields.gender[.option.*] above.
    'staff.modal.addTitle': 'إضافة موظف',
    'staff.modal.editTitle': 'تعديل موظف',
    'staff.modal.sendingInvitation': 'جارٍ إرسال الدعوة...',
    'staff.modal.saving': 'جارٍ الحفظ...',
    'staff.modal.sending': 'جارٍ الإرسال...',
    'staff.modal.add': 'إضافة',
    'staff.modal.save': 'حفظ',
    'staff.modal.cancel': 'إلغاء',
    'staff.modal.delete': 'حذف الموظف',
    'staff.fields.fullName': 'الاسم الكامل',
    'staff.fields.fullName.placeholder': 'الاسم الكامل للموظف',
    'staff.fields.email': 'البريد الإلكتروني',
    'staff.fields.email.placeholder': 'عنوان البريد الإلكتروني',
    'staff.fields.phone': 'رقم الهاتف',
    'staff.fields.phone.placeholder': 'رقم الهاتف',
    'staff.fields.position': 'المسمّى الوظيفي',
    'staff.fields.position.placeholder': 'مثال: مصفّف شعر، مدير، إلخ.',
    'staff.fields.gender.placeholder': 'اختيار الجنس',
    'staff.fields.role': 'الدور',
    'staff.fields.role.placeholder': 'اختيار الدور',
    'staff.fields.role.option.manager': 'مدير الموظفين',
    'staff.fields.role.option.staff': 'موظف',
    'staff.status.active': 'نشط',
    'staff.status.inactive': 'غير نشط',
    'staff.schedule.title': 'جدول العمل',
    'staff.schedule.notAvailable': 'غير متاح',
    'staff.errors.fullNameRequired': 'الاسم الكامل مطلوب',
    'staff.errors.emailRequired': 'البريد الإلكتروني مطلوب',
    'staff.errors.emailInvalid': 'البريد الإلكتروني غير صالح',
    'staff.errors.phoneRequired': 'رقم الهاتف مطلوب',
    // AB#1842 (PBI-F) — Arabic strings for the salon-gender hard-block flow.
    // Tone: bilingual EN/AR; gender-neutral plural forms per Devanza saved
    // Arabic rule. {n}/{type} interpolations are substituted at render-time.
    'staff.invite.genderTooltip.womenOnly': 'هذا الصالون مخصّص للسيدات فقط؛ يمكن دعوة الموظفات فقط.',
    'staff.invite.genderTooltip.menOnly': 'هذا الصالون مخصّص للرجال فقط؛ يمكن دعوة الموظفين فقط.',
    'staff.errors.genderMismatchesSalon': 'جنس هذا الموظف لا يطابق إعدادات الصالون.',
    // AB#1862 — Arabic strings for the booking-conflict guard. MSA register,
    // gender-neutral verbal-noun / passive pattern per devanza-web AR
    // convention (owner may be female). {count} / {earliest} are
    // interpolated at render-time. Reviewed by `arabic-translator` skill.
    'staff.reassign.conflict.title': 'حجوزات قادمة متأثرة',
    'staff.reassign.conflict.body': 'لدى هذا الموظف {count} موعد قادم في الفروع التي يجري إلغاء تعيينه فيها. أقربها بتاريخ {earliest}. المتابعة ستبقي هذه الحجوزات في التقويم، مع تسجيل الموظف على أنه لم يعد معيَّنًا في تلك الفروع.',
    'staff.reassign.conflict.body.services': 'لدى هذا الموظف {count} موعد قادم للخدمات التي يجري إلغاء تخصيصه لها. أقربها بتاريخ {earliest}. المتابعة ستبقي هذه الحجوزات في التقويم، مع تسجيل الموظف على أنه لم يعد قادرًا على تقديم تلك الخدمات.',
    'staff.reassign.conflict.viewCalendar': 'مراجعة في التقويم',
    // AB#1976 (CR-2026-07-04) — إدارة غياب الموظفين. بانتظار مراجعة لجنة الترجمة.
    'calendar.staffMenu.aria': 'إجراءات {name}',
    'calendar.staffMenu.bookWith': 'الحجز مع {name}…',
    'calendar.staffMenu.addBreak': 'إضافة استراحة أو إجازة…',
    'calendar.staffMenu.viewStatus': 'عرض التوفّر',
    // AB#2022 — colour-coded availability on the calendar.
    'calendar.absence.unavailable': 'غير متاح — {type}. لا يمكنك الحجز هنا.',
    'calendar.legend.onLeave': 'في إجازة (يوم كامل)',
    'calendar.legend.onBreak': 'في استراحة',
    // AB#2023 — at-a-glance availability pills + "who's free at" check.
    'calendar.avail.available': 'متاح',
    'calendar.avail.onLeave': 'في إجازة اليوم',
    'calendar.avail.off': 'غير مجدول اليوم',
    'calendar.avail.onBreak': 'في استراحة · حتى {time}',
    'calendar.avail.booked': 'محجوز',
    'calendar.avail.nextFree': 'يتفرّغ {time}',
    'calendar.avail.offHours': 'خارج الدوام',
    'calendar.avail.reason.break': 'في استراحة',
    'calendar.avail.reason.leave': 'في إجازة',
    'calendar.avail.reason.booked': 'محجوز',
    'calendar.avail.reason.off': 'خارج الدوام',
    'calendar.avail.checkLabel': 'من المتاح الساعة',
    'calendar.avail.checkClear': 'مسح',
    'calendar.staffMenu.viewSchedule': 'عرض جدول {name} الكامل',
    // AB#2019 — read-only staff availability status panel.
    'staff.status.title': 'التوفّر',
    'staff.status.today': 'اليوم',
    'staff.status.upcoming': 'القادم',
    'staff.status.working': 'يعمل {range}',
    'staff.status.off': 'غير مجدول للعمل اليوم',
    'staff.status.noUpcoming': 'لا توجد إجازات أو استراحات قادمة',
    'staff.status.loading': 'جارٍ التحميل…',
    'staff.status.close': 'إغلاق',
    // AB#2021 — quick edit/remove of today's breaks from the panel.
    'staff.status.editBreak': 'تعديل الاستراحة',
    'staff.status.removeBreak': 'إزالة الاستراحة',
    'staff.status.removeConfirm': 'إزالة هذه الاستراحة؟',
    'calendar.staffMenu.manage': 'إدارة {name}',
    // P0-3 (AB#2043) — appointment status operations from the calendar peek popover.
    'calendar.peek.updateStatus': 'تحديث الحالة',
    'calendar.peek.status.arrived': 'وصل',
    'calendar.peek.status.started': 'بدأ',
    'calendar.peek.status.completed': 'مكتمل',
    'calendar.peek.status.noshow': 'لم يحضر',
    'calendar.peek.statusUpdated': 'تم تحديث الحالة',
    'calendar.peek.statusUpdateFailed': 'تعذّر تحديث الحالة',
    'calendar.peek.close': 'إغلاق',
    // AB#2164 — owner Confirm / Decline / Cancel booking from the calendar peek popover.
    'calendar.peek.action.confirm': 'تأكيد',
    'calendar.peek.action.decline': 'رفض',
    'calendar.peek.action.cancel': 'إلغاء الحجز',
    'calendar.peek.action.keepBooking': 'الإبقاء على الحجز',
    'calendar.peek.statusConfirmed': 'مؤكد',
    'calendar.peek.declinePrompt': 'رفض هذا الحجز؟ سيتم إشعار العميل.',
    'calendar.peek.cancelPrompt': 'إلغاء هذا الحجز؟ سيتم إشعار العميل.',
    'calendar.peek.toast.confirmed': 'تم تأكيد الحجز',
    'calendar.peek.toast.declined': 'تم رفض الحجز',
    'calendar.peek.toast.cancelled': 'تم إلغاء الحجز',

    // O1 (AB#2199) — shared booking lifecycle words (founder terminology 2026-11-04).
    'booking.status.pending': 'بانتظار التأكيد',
    'booking.status.confirmed': 'مؤكد',
    'booking.status.arrived': 'وصل',
    'booking.status.started': 'بدأ',
    'booking.status.completed': 'مكتمل',
    'booking.status.noShow': 'لم يحضر',
    'booking.status.cancelled': 'ملغى',
    'booking.status.declinedBySalon': 'رفضه الصالون',
    'booking.status.cancelledByCustomer': 'ألغاه العميل',
    'booking.status.cancelledBySalon': 'ألغاه الصالون',
    'booking.status.unknown': 'حالة غير معروفة',
    'booking.reason.expired': 'لم يُؤكَّد الطلب قبل موعد الحجز.',
    'booking.reason.admin': 'ألغاه فريق دعم Devanza.',
    'booking.detail.title': 'تفاصيل الحجز',
    'booking.detail.customer': 'العميل',
    'booking.detail.phone': 'الهاتف',
    'booking.detail.date': 'التاريخ',
    'booking.detail.time': 'الوقت',
    'booking.detail.staff': 'عضو الفريق',
    'booking.detail.branch': 'الفرع',
    'booking.detail.payment': 'الدفع',
    'booking.detail.walkIn': 'زيارة بدون موعد',
    'booking.payment.paid': 'مدفوع',
    'booking.payment.unpaid': 'غير مدفوع',
    'booking.payment.refunded': 'مسترد',
    'booking.action.markPaid': 'تسجيل الدفع',
    'booking.action.openCalendar': 'فتح في التقويم',
    'calendar.loading': 'جارٍ تحميل التقويم…',
    'app.loading': 'جارٍ التحميل…',
    'session.expired': 'انتهت جلستك. يُرجى تسجيل الدخول مجددًا.',
    'calendar.unassigned': 'غير مُسنَد',
    'calendar.unassignedHint': 'خارج جدول فريق اليوم',
    'calendar.noStaff': 'لا يوجد موظفون في هذا الفرع',
    'calendar.card.awaitingConfirmation': 'بانتظار التأكيد',
    'common.comingSoon': 'قريبًا',
    'staff.absence.title': 'الاستراحات والإجازات',
    'staff.absence.add': 'إضافة',
    'staff.absence.editBreak': 'تعديل الاستراحة',
    'staff.absence.loading': 'جارٍ التحميل…',
    'staff.absence.empty': 'لا توجد استراحات أو إجازات بعد. أضف واحدة لتنظيم أوقات التوفر.',
    'staff.absence.remove': 'إزالة',
    'staff.absence.recurring': 'يتكرر يوميًا',
    'staff.absence.save': 'حفظ',
    'staff.absence.cancel': 'إلغاء',
    'staff.absence.type.break': 'استراحة',
    'staff.absence.type.sick': 'إجازة مرضية',
    'staff.absence.type.vacation': 'إجازة سنوية',
    'staff.absence.type.personal': 'إجازة شخصية',
    'staff.absence.type.emergency': 'حالة طارئة',
    'staff.absence.reason.lunch': 'الغداء',
    'staff.absence.reason.prayer': 'الصلاة',
    'staff.absence.reason.rest': 'استراحة قصيرة',
    'staff.absence.reason.medical': 'موعد طبي',
    'staff.absence.reason.other': 'أخرى',
    'staff.absence.field.type': 'النوع',
    'staff.absence.field.branch': 'الفرع',
    'staff.absence.field.date': 'التاريخ',
    'staff.absence.field.startTime': 'وقت البداية',
    'staff.absence.field.endTime': 'وقت النهاية',
    'staff.absence.field.startDate': 'تاريخ البداية',
    'staff.absence.field.endDate': 'تاريخ النهاية',
    'staff.absence.field.reason': 'السبب',
    'staff.absence.field.recurring': 'التكرار كل يوم',
    'staff.absence.toast.saved': 'تمت الإضافة',
    'staff.absence.toast.saveFailed': 'تعذّر الحفظ',
    'staff.absence.toast.deleted': 'تمت الإزالة',
    'staff.absence.toast.deleteFailed': 'تعذّر الإزالة',
    'staff.absence.error.branchRequired': 'اختر فرعًا لهذه الفترة',
    'staff.absence.error.dateOrder': 'يجب أن يكون تاريخ النهاية في أو بعد تاريخ البداية',
    'staff.absence.error.timeOrder': 'يجب أن يكون وقت النهاية بعد وقت البداية',
    'staff.absence.hours.works': 'ساعات العمل: {range}',
    'staff.absence.hours.off': 'لا يعمل في هذا اليوم',
    'staff.absence.hours.warnOutside': 'خارج ساعات عمل الموظف في هذا اليوم.',
    'staff.absence.error.outsideHours': 'هذا الوقت خارج ساعات عمل الموظف في هذا اليوم. اختر وقتًا ضمن ساعاته أو حدّث جدوله.',
    'staff.absence.error.overlap': 'لدى الموظف غياب آخر يتداخل مع هذا الوقت. عدّله أو احذفه أولًا.',
    'staff.absence.error.overlapDetail': 'يتداخل مع غياب حالي — {details}. عدّله أو احذفه أولًا.',
    'staff.absence.error.leaveOverlap': 'لدى هذا الموظف إجازة مُسجّلة في هذه الفترة — {details}. اختر تواريخ لا تتداخل معها.',
    // AB#2024 — break policy caps (per branch).
    'staff.absence.break.allowance': 'رصيد الاستراحة اليوم: {used}/{total} دقيقة · {n} من {max} · هذه الاستراحة: {this} دقيقة',
    'staff.absence.break.tooLong': 'لا يمكن أن تتجاوز الاستراحة {max} دقيقة. ',
    'staff.absence.break.switchToPersonal': 'التحويل إلى إجازة شخصية',
    'staff.absence.break.overLimit.title': 'تجاوز حد الاستراحة',
    'staff.absence.break.overLimit.body': 'يتجاوز هذا حد استراحات الفرع ({maxBreaks} استراحة / {maxMin} دقيقة يوميًا). استُخدمت {used} دقيقة اليوم بالفعل. الإضافة على أي حال؟',
    // AB#2024 — per-branch break & absence rules editor.
    'branch.absencePolicy.button': 'قواعد الاستراحات',
    'branch.absencePolicy.title': 'قواعد الاستراحات والغياب',
    'branch.absencePolicy.loading': 'جارٍ التحميل…',
    'branch.absencePolicy.maxBreaks': 'عدد الاستراحات في اليوم',
    'branch.absencePolicy.maxBreaks.help': 'كم استراحة منفصلة يمكن للموظف أخذها في اليوم.',
    'branch.absencePolicy.maxTotal': 'إجمالي وقت الاستراحة يوميًا (دقيقة)',
    'branch.absencePolicy.maxTotal.help': 'إجمالي دقائق الاستراحة المسموح بها لكل موظف يوميًا.',
    'branch.absencePolicy.maxSingle': 'أطول استراحة واحدة (دقيقة)',
    'branch.absencePolicy.maxSingle.help': 'أي استراحة أطول من ذلك يجب تسجيلها كإجازة شخصية.',
    'branch.absencePolicy.override': 'السماح للمالك بتجاوز الحدود',
    'branch.absencePolicy.override.help': 'يمكن للمالك تجاوز هذه الحدود بعد التأكيد.',
    'branch.absencePolicy.save': 'حفظ القواعد',
    'branch.absencePolicy.cancel': 'إلغاء',
    'branch.absencePolicy.saved': 'تم حفظ قواعد الاستراحات',
    'branch.absencePolicy.saveFailed': 'تعذّر حفظ قواعد الاستراحات',
    'staff.absence.conflict.title': 'حجوزات في هذه الفترة',
    'staff.absence.conflict.body': 'لدى الموظف {count} حجز خلال فترة هذا الغياب. الحفظ سيتركها بدون مقدّم خدمة — أعد إسنادها أو ألغِها أولًا.',
    'staff.absence.conflict.more': '+{count} إضافية',
    'staff.absence.conflict.saveAnyway': 'الحفظ على أي حال',
    'staff.absence.conflict.cancel': 'إلغاء',
    'staff.reassign.conflict.forceProceed': 'المتابعة على أي حال',
    'staff.reassign.conflict.cancel': 'إلغاء',
    'staff.reassign.conflict.forcedToast': 'تم الحفظ. لا تزال {count} حجوزات على تقويم الموظف — يمكن مراجعتها من التقويم.',
    'staff.reassign.conflict.deleteTitle': 'تعذّر الحذف — حجوزات مسجّلة',
    'staff.reassign.conflict.deleteBody': 'لدى هذا الموظف {count} موعد قادم (أقربها بتاريخ {earliest}). يرجى إلغاء هذه المواعيد أو إسنادها إلى موظف آخر قبل حذف الموظف.',
    'staff.reassign.conflict.deleteClose': 'حسنًا',
    'staff.reassign.conflict.networkRetry': 'تعذّر الحفظ. يرجى التحقق من الاتصال والمحاولة مرة أخرى.',
    'business.serviceType.flip.blocker.title.womenOnly': 'لا يمكن الحفظ: {n} موظف ذكر يعيقون هذا التغيير',
    'business.serviceType.flip.blocker.title.menOnly': 'لا يمكن الحفظ: {n} موظفة يعقن هذا التغيير',
    'business.serviceType.flip.blocker.body': 'يتطلب التحويل إلى "{type}" أن يطابق جميع الموظفين هذا الجنس. الموظفون التالون لا يطابقون:',
    'business.serviceType.flip.blocker.instructions': 'يمكنك حل ذلك بإزالتهم أو نقلهم، ثم حاول مجددًا.',
    'business.serviceType.flip.blocker.cta.resolve': 'حلّ أولًا → إدارة الموظفين',
    'business.serviceType.flip.blocker.cta.cancel': 'إلغاء',
    'business.serviceType.flip.blocker.override.link': 'طلب استثناء من المسؤول العام',
    'business.serviceType.flip.override.title': 'طلب استثناء من المسؤول العام',
    'business.serviceType.flip.override.prompt': 'لماذا يحتاج صالونك إلى استثناء؟ (٢٠ حرفًا على الأقل.)',
    'business.serviceType.flip.override.placeholder': 'اشرح الدور ولماذا يُعد الاستثناء مناسبًا…',
    'business.serviceType.flip.override.disclaimer': 'سيقوم مسؤول Devanza بمراجعة طلبك. سنرسل لك بريدًا إلكترونيًا عند البت فيه.',
    'business.serviceType.flip.override.submit': 'إرسال الطلب',
    'business.serviceType.flip.override.cancel': 'إلغاء',
    'business.serviceType.flip.override.tooShort': 'يرجى كتابة ٢٠ حرفًا على الأقل لشرح الاستثناء.',
    'business.serviceType.flip.override.counter': '{count} / ٢٠ كحد أدنى',
    'business.serviceType.flip.override.submitted': 'تم تقديم طلب الاستثناء. سنرد خلال يومي عمل.',
    'business.serviceType.flip.override.error': 'تعذّر إرسال الطلب. يرجى المحاولة مرة أخرى.',
    'business.serviceType.womenOnly': 'نسائي',
    'business.serviceType.menOnly': 'رجالي',
    'business.serviceType.everyone': 'للجميع',
    'nav.genderOverrides': 'استثناءات الجنس',
    'superAdmin.overrides.title': 'استثناءات تعارض الجنس',
    'superAdmin.overrides.subtitle': 'طلبات من أصحاب الصالونات للإبقاء على موظف لا يطابق جنسه إعدادات الصالون.',
    'superAdmin.overrides.tab.pending': 'قيد المراجعة',
    'superAdmin.overrides.tab.history': 'السجل',
    'superAdmin.overrides.empty.pending': 'لا توجد طلبات استثناء قيد المراجعة.',
    'superAdmin.overrides.empty.history': 'لم تُسجَّل أي قرارات بعد.',
    'superAdmin.overrides.loading': 'جارٍ تحميل الاستثناءات…',
    'superAdmin.overrides.error': 'تعذّر تحميل الاستثناءات. يرجى التحديث والمحاولة مرة أخرى.',
    'superAdmin.overrides.row.business': 'النشاط',
    'superAdmin.overrides.row.staff': 'الموظف',
    'superAdmin.overrides.row.requestedBy': 'مقدّم الطلب',
    'superAdmin.overrides.row.justification': 'المبرّر',
    'superAdmin.overrides.row.decidedBy': 'قرّره',
    'superAdmin.overrides.row.decidedAt': 'تاريخ القرار',
    'superAdmin.overrides.row.adminReason': 'السبب',
    'superAdmin.overrides.action.approve': 'موافقة',
    'superAdmin.overrides.action.reject': 'رفض',
    'superAdmin.overrides.status.pending': 'قيد المراجعة',
    'superAdmin.overrides.status.approved': 'موافَق عليه',
    'superAdmin.overrides.status.rejected': 'مرفوض',
    'superAdmin.overrides.decide.approve.title': 'الموافقة على هذا الاستثناء؟',
    'superAdmin.overrides.decide.reject.title': 'رفض هذا الاستثناء؟',
    'superAdmin.overrides.decide.reasonLabel': 'السبب (٢٠ حرفًا على الأقل)',
    'superAdmin.overrides.decide.reasonPlaceholder': 'لماذا يُعد هذا القرار مناسبًا؟ (يظهر للمالك.)',
    'superAdmin.overrides.decide.counter': '{count} / ٢٠ كحد أدنى',
    'superAdmin.overrides.decide.confirm.approve': 'الموافقة على الاستثناء',
    'superAdmin.overrides.decide.confirm.reject': 'رفض الاستثناء',
    'superAdmin.overrides.decide.cancel': 'إلغاء',
    'superAdmin.overrides.decide.tooShort': 'يرجى كتابة ٢٠ حرفًا على الأقل لشرح القرار.',
    'superAdmin.overrides.decide.toast.approved': 'تمت الموافقة على الاستثناء.',
    'superAdmin.overrides.decide.toast.rejected': 'تم رفض الاستثناء.',
    'superAdmin.overrides.decide.toast.error': 'تعذّر حفظ قرارك. يرجى المحاولة مرة أخرى.',
    // ── AB#2045 — منح وصول التأسيس (اشتراك مجاني) — SuperAdmin ──────────
    // مسودة عربية فصحى حديثة. أسماء الباقات (Starter/Growth/Pro/Enterprise)
    // تبقى بالحروف اللاتينية وفق قاعدة أسماء الباقات.
    'superadmin.owners.grant.action.open': 'منح وصول التأسيس',
    'superadmin.owners.grant.title': 'منح وصول التأسيس',
    'superadmin.owners.grant.forOwner': 'تفعيل اشتراك مجاني لـ',
    'superadmin.owners.grant.business': 'النشاط التجاري',
    'superadmin.owners.grant.planTier': 'مستوى الباقة',
    'superadmin.owners.grant.planTier.hint': 'باقة Enterprise ترفع حدّ الفروع ليتمكّن الصالون من تشغيل عدّة فروع.',
    'superadmin.owners.grant.compDays': 'المدّة (بالأيام)',
    'superadmin.owners.grant.compDays.hint': 'مدّة النافذة المجانية، من 1 إلى 3650 يوماً. الافتراضي 365.',
    'superadmin.owners.grant.reason': 'السبب (اختياري)',
    'superadmin.owners.grant.reason.placeholder': 'مثال: شريك تأسيسي، تجربة إطلاق',
    'superadmin.owners.grant.compNotice': 'هذا منح مجاني غير مفوتر. لا يتم خصم أي بطاقة ويُستثنى من تقارير الإيرادات.',
    'superadmin.owners.grant.noBusiness': 'لا يملك هذا المالك أي نشاط تجاري بعد، لذا لا يوجد ما يمكن تفعيله.',
    'superadmin.owners.grant.action.cancel': 'إلغاء',
    'superadmin.owners.grant.action.submit': 'منح الوصول',
    'superadmin.owners.grant.toast.granted': 'تم منح وصول التأسيس.',
    'superadmin.owners.grant.toast.already': 'يمتلك هذا النشاط التجاري وصولاً مجانياً بالفعل.',
    'superadmin.owners.grant.toast.error': 'تعذّر منح الوصول. يرجى المحاولة مرة أخرى.',
    'superadmin.owners.grant.error.conflict': 'يمتلك هذا النشاط التجاري اشتراكاً مدفوعاً بالفعل، لذا لم يُطبَّق المنح المجاني.',
    // ── AB#2112 — تفعيل / إلغاء تفعيل الحساب (تأكيد). معتمد من المترجم 2026-10-28 ──
    'superadmin.accountStatus.title.deactivate': 'إلغاء تفعيل الحساب؟',
    'superadmin.accountStatus.title.activate': 'تفعيل الحساب؟',
    'superadmin.accountStatus.account': 'الحساب',
    'superadmin.accountStatus.body.deactivate': 'سيتم تسجيل خروج هذا الحساب من جميع الأجهزة فوراً، ولن يمكن استخدامه لتسجيل الدخول حتى يُعاد تفعيله.',
    'superadmin.accountStatus.body.activate': 'سيصبح بالإمكان استخدام هذا الحساب لتسجيل الدخول مجدداً. يلزم تسجيل دخول جديد، فالجلسات السابقة تبقى منتهية.',
    'superadmin.accountStatus.ownerNote': 'لن يتأثر الصالون: تبقى صفحته وحجوزاته كما هي. لإيقاف الصالون نفسه، استخدم «حالة الصالون».',
    'superadmin.accountStatus.action.cancel': 'إلغاء',
    'superadmin.accountStatus.action.close': 'إغلاق',
    'superadmin.accountStatus.action.deactivate': 'إلغاء التفعيل',
    'superadmin.accountStatus.action.activate': 'تفعيل',
    'superadmin.accountStatus.toast.deactivated': 'تم إلغاء تفعيل الحساب.',
    'superadmin.accountStatus.toast.activated': 'تم تفعيل الحساب.',
    'superadmin.accountStatus.toast.unchanged': 'لم يتغيّر شيء: الحساب على هذه الحالة أصلاً.',
    'superadmin.accountStatus.error.self': 'لا يمكنك إلغاء تفعيل حسابك.',
    'superadmin.accountStatus.error.lastSuperAdmin': 'يجب أن يبقى حساب مسؤول رئيسي نشط واحد على الأقل.',
    'superadmin.accountStatus.error.notFound': 'هذا الحساب لم يعد موجوداً. حدّث القائمة.',
    'superadmin.accountStatus.error.generic': 'تعذّر تحديث الحساب. يُرجى المحاولة مرة أخرى.',
    // P0-7 (AB#2047) — لوحة الحجوزات الأخيرة للقراءة فقط لدعم المشرف العام (AR).
    'superadmin.bookings.action.open': 'عرض الحجوزات',
    'superadmin.bookings.title': 'الحجوزات الأخيرة',
    'superadmin.bookings.forOwner': 'حجوزات',
    'superadmin.bookings.business': 'النشاط التجاري',
    'superadmin.bookings.close': 'إغلاق',
    'superadmin.bookings.retry': 'إعادة المحاولة',
    'superadmin.bookings.loading': 'جارٍ تحميل الحجوزات…',
    'superadmin.bookings.empty': 'لا توجد حجوزات لهذا النشاط التجاري.',
    'superadmin.bookings.error': 'تعذّر تحميل الحجوزات. يرجى المحاولة مرة أخرى.',
    'superadmin.bookings.col.dateTime': 'التاريخ والوقت',
    'superadmin.bookings.col.service': 'الخدمة',
    'superadmin.bookings.col.staff': 'الموظف',
    'superadmin.bookings.col.customer': 'العميل',
    'superadmin.bookings.col.phone': 'الهاتف',
    'superadmin.bookings.col.status': 'الحالة',
    'superadmin.bookings.col.amount': 'المبلغ',
    'superadmin.bookings.status.booked': 'محجوز',
    'superadmin.bookings.status.approved': 'موافَق عليه',
    'superadmin.bookings.status.completed': 'مكتمل',
    'superadmin.bookings.status.cancelled': 'ملغى',
    'superadmin.bookings.status.noshow': 'لم يحضر',
    'superadmin.bookings.status.arrived': 'وصل',
    'superadmin.bookings.status.started': 'بدأ',
    // Batch C (AB#2195) — Super Admin booking support, salon suspension, owner suspension banner (AR).
    'nav.bookingSupport': 'دعم الحجوزات',
    'approval.banner.suspended': 'علّقت Devanza صالونك. لا يمكن للعملاء العثور عليه أو الحجز فيه حاليًا. تبقى حجوزاتك الحالية في تقويمك. تواصل مع دعم Devanza إن كانت لديك أي أسئلة.',
    'superadmin.users.searchPlaceholder': 'ابحث بالاسم أو البريد الإلكتروني أو رقم الهاتف…',
    'superadmin.support.title': 'دعم الحجوزات',
    'superadmin.support.subtitle': 'ابحث عن حجز العميل برقم الحجز أو رقم الهاتف، وألغِه عند الحاجة.',
    'superadmin.support.search.bookingNumber': 'رقم الحجز',
    'superadmin.support.search.phone': 'هاتف العميل',
    'superadmin.support.search.phoneHint': 'بأي صيغة، مثل ⁦+971 50 123 4567⁩ أو ⁦050 123 4567⁩.',
    'superadmin.support.search.submit': 'بحث',
    'superadmin.support.search.clear': 'مسح',
    'superadmin.support.search.prompt': 'أدخل رقم الحجز أو رقم هاتف العميل للعثور على الحجز.',
    'superadmin.support.search.empty': 'لا توجد حجوزات مطابقة لهذا البحث.',
    'superadmin.support.search.error.required': 'أدخل رقم الحجز أو رقم هاتف العميل.',
    'superadmin.support.search.error.bookingNumber': 'يجب أن يتكوّن رقم الحجز من أرقام فقط.',
    'superadmin.support.search.error.phone': 'أدخل 7 أرقام على الأقل من رقم الهاتف.',
    'superadmin.support.search.error.generic': 'تعذّر البحث في الحجوزات. يُرجى المحاولة مرة أخرى.',
    'superadmin.support.col.booking': 'الحجز',
    'superadmin.support.col.salon': 'الصالون',
    'superadmin.support.col.actions': 'الإجراءات',
    'superadmin.support.action.cancel': 'إلغاء الحجز',
    'superadmin.support.cancel.title': 'إلغاء هذا الحجز؟',
    'superadmin.support.cancel.body': 'سيُبلَغ الصالون بأن دعم Devanza ألغى الحجز، وكذلك العميل إن كان قد حجز من حسابه في Devanza. يصبح الموعد متاحًا من جديد. لا يمكن التراجع عن ذلك.',
    'superadmin.support.reason.label': 'السبب',
    'superadmin.support.reason.hint': 'مطلوب. يُحفظ في سجل التدقيق ولا يظهر للعميل أو للصالون.',
    'superadmin.support.cancel.reason.placeholder': 'مثال: إغلاق الصالون لظرف طارئ',
    'superadmin.support.cancel.keep': 'الإبقاء على الحجز',
    'superadmin.support.cancel.confirm': 'إلغاء الحجز',
    'superadmin.support.cancel.toast.done': 'أُلغي الحجز، وأُبلغ الصالون.',
    'superadmin.support.cancel.error.notOpen': 'لا يمكن إلغاء هذا الحجز لأنه لم يعد بانتظار التأكيد أو مؤكدًا. حدّث القائمة.',
    'superadmin.support.cancel.error.reasonRequired': 'أدخل السبب.',
    'superadmin.support.cancel.error.reasonTooLong': 'يجب ألا يتجاوز السبب 500 حرف.',
    'superadmin.support.cancel.error.notFound': 'هذا الحجز لم يعد موجودًا. حدّث القائمة.',
    'superadmin.support.cancel.error.generic': 'تعذّر إلغاء الحجز. يُرجى المحاولة مرة أخرى.',
    'superadmin.salonStatus.action.open': 'حالة الصالون',
    'superadmin.salonStatus.title': 'حالة الصالون',
    'superadmin.salonStatus.current': 'الحالة',
    'superadmin.salonStatus.status.live': 'منشور',
    'superadmin.salonStatus.status.suspended': 'معلّق',
    'superadmin.salonStatus.status.pending': 'بانتظار الموافقة',
    'superadmin.salonStatus.status.draft': 'مسودّة',
    'superadmin.salonStatus.status.rejected': 'مطلوب تعديلات',
    'superadmin.salonStatus.suspend.body': 'يؤدي التعليق إلى إخفاء الصالون عن العملاء وإيقاف الحجوزات الجديدة فورًا. يُبلَغ صاحب الصالون بذلك مع السبب. لا تُلغى الحجوزات الحالية، بل تظهر هنا لتتعامل معها.',
    'superadmin.salonStatus.suspend.reason.placeholder': 'مثال: شكاوى متكررة من العملاء',
    'superadmin.salonStatus.suspend.reason.hint': 'مطلوب. سيرى صاحب الصالون هذا السبب، لذا لا تذكر فيه بيانات العملاء.',
    'superadmin.salonStatus.reinstate.body': 'تؤدي إعادة التفعيل إلى إظهار الصالون وإتاحة الحجز فيه من جديد. يُبلَغ صاحب الصالون بذلك.',
    'superadmin.salonStatus.notLiveNote': 'لا يمكن تعليق إلا الصالون المنشور. هذا الصالون غير منشور.',
    'superadmin.salonStatus.action.suspend': 'تعليق الصالون',
    'superadmin.salonStatus.action.reinstate': 'إعادة تفعيل الصالون',
    'superadmin.salonStatus.action.suspending': 'جارٍ التعليق…',
    'superadmin.salonStatus.action.reinstating': 'جارٍ إعادة التفعيل…',
    'superadmin.salonStatus.owner': 'صاحب الصالون',
    'superadmin.support.cancel.cancelling': 'جارٍ الإلغاء…',
    'superadmin.salonStatus.toast.suspended': 'عُلّق الصالون، وسيُبلَغ صاحبه.',
    'superadmin.salonStatus.toast.reinstated': 'أُعيد تفعيل الصالون، وسيُبلَغ صاحبه.',
    'superadmin.salonStatus.openBookings.title': 'الحجوزات القادمة المفتوحة',
    'superadmin.salonStatus.openBookings.hint': 'لم تُلغَ هذه الحجوزات. تواصل مع العملاء، أو ألغِ أي حجز لا يمكن إتمامه.',
    'superadmin.salonStatus.openBookings.empty': 'لا توجد حجوزات قادمة مفتوحة.',
    'superadmin.salonStatus.error.notLive': 'لا يمكن تعليق إلا الصالون المنشور. حدّث القائمة.',
    'superadmin.salonStatus.error.notSuspended': 'هذا الصالون غير معلّق. حدّث القائمة.',
    'superadmin.salonStatus.error.reasonRequired': 'أدخل السبب.',
    'superadmin.salonStatus.error.notFound': 'هذا الصالون لم يعد موجودًا. حدّث القائمة.',
    'superadmin.salonStatus.error.generic': 'تعذّر تحديث حالة الصالون. يُرجى المحاولة مرة أخرى.',
    // ── AB#1904 (W-1) — Stripe Checkout upgrade flow (AR draft) ────────
    // Draft Modern Standard Arabic. Final sign-off by arabic-translator +
    // content-review-committee per Devanza always-AR-review rule. Brand
    // names (Stripe, Starter, Growth, Pro, Enterprise, Devanza) stay in
    // Latin script per memory rule. Money format: {amount} {currency} in AR.
    'subscription.confirm.title': 'الترقية إلى {planName}',
    'subscription.confirm.currentLabel': 'باقتك الحالية',
    'subscription.confirm.targetLabel': 'الباقة الجديدة',
    'subscription.confirm.delta': '+{amount} {currency}/شهر',
    'subscription.confirm.prorationHead': 'الدفعة اليوم',
    'subscription.confirm.prorationBody': 'سيتم تحصيل مبلغ تناسبي اليوم عن المدة المتبقية من دورة الفوترة الحالية. ومن الفاتورة التالية، سيتم تحصيل {amount} {currency} شهرياً.',
    'subscription.confirm.stripeNote': 'سيتم تحويلك إلى صفحة الدفع الآمنة في Stripe لإتمام العملية.',
    'subscription.confirm.continue': 'المتابعة إلى الدفع',
    'subscription.confirm.cancel': 'إلغاء',
    'subscription.confirm.submitting': 'جارٍ فتح صفحة الدفع الآمنة...',
    'subscription.confirm.errorMint': 'تعذّر فتح صفحة الدفع. يرجى إعادة المحاولة، وإذا تكرّر الأمر تواصل مع الدعم.',
    'subscription.confirm.alreadyOnPlan': 'أنت بالفعل على باقة {planName}.',
    'subscription.confirm.close': 'إغلاق',
    'subscription.redirecting.title': 'جارٍ فتح صفحة الدفع الآمنة...',
    'subscription.redirecting.sub': 'يتم تحويلك بشكل آمن إلى Stripe.',
    'subscription.success.verifyingTitle': 'جارٍ تأكيد ترقيتك...',
    'subscription.success.verifyingBody': 'عادةً ما تستغرق ثوانٍ معدودة.',
    'subscription.success.title': 'مرحباً بك في باقة {planName}!',
    'subscription.success.sub': 'باقتك الجديدة فعّالة الآن. وهذه هي الميزات التي فُتحت لك:',
    'subscription.success.ctaMyBusiness': 'الانتقال إلى أعمالي',
    'subscription.success.ctaDashboard': 'الانتقال إلى لوحة التحكم',
    'subscription.success.receiptFooter': 'تم إرسال الإيصال إلى {email}. يمكنك مراجعة الفواتير السابقة في أي وقت من',
    'subscription.success.receiptFooterLink': 'صفحة الاشتراك',
    'subscription.success.processingTitle': 'تم الدفع بنجاح — نحن في انتظار التأكيد فقط.',
    'subscription.success.processingSub': 'قد يستغرق ذلك بضع ثوانٍ، وستُحدّث الصفحة تلقائياً.',
    'subscription.success.pollAttempt': 'المحاولة {n} من 15',
    'subscription.success.timeoutTitle': 'اقتربنا — لا تزال ترقيتك قيد المعالجة.',
    'subscription.success.timeoutBody': 'تم الدفع بنجاح، لكن لم يصلنا التأكيد بعد من معالج الدفع، ويتمّ ذلك عادةً خلال دقيقة.',
    'subscription.success.timeoutRefresh': 'تحديث الآن',
    'subscription.success.timeoutContinue': 'المتابعة إلى لوحة التحكم',
    'subscription.success.timeoutFooter': 'إذا بقيت باقتك تظهر كـ {oldPlanName} بعد دقيقة، تواصل مع فريق ديفانزا مع ذكر هذا الرمز: {session_id}.',
    'subscription.success.errorTitle': 'تعذّر تأكيد ترقيتك.',
    'subscription.success.errorBody': 'تم الدفع عبر Stripe بنجاح، لكن لم نتمكّن من ربط العملية بحسابك تلقائياً. لا تقلق — لم يُفقد أي مبلغ.',
    'subscription.success.errorContact': 'تواصل مع الدعم',
    // AB#2133 — pilot support path (founder-configured contact; no hard-coded address).
    'support.contact.title': 'التواصل مع فريق ديفانزا',
    'support.contact.emailUs': 'عبر البريد الإلكتروني',
    'support.contact.whatsappUs': 'عبر واتساب',
    'support.contact.notConfigured': 'نعمل على تجهيز وسيلة التواصل مع الدعم. يُرجى التواصل مع مدير حسابك في ديفانزا.',
    'help.faq.contactSupport.pilotAnswer': 'راسل فريق دعم Devanza عبر بيانات التواصل في أسفل هذه الصفحة (وتجدها أيضًا في صفحة الدعم من الشريط الجانبي).',
    'help.needMoreHelp.pilotBody': 'لم تجد إجابتك؟ تواصل مع فريق دعم Devanza:',
    'subscription.success.errorBack': 'العودة إلى الباقات',
    'subscription.cancelled.title': 'لا بأس — لم يتمّ تحصيل أي مبلغ.',
    'subscription.cancelled.sub': 'تستمر باقتك {planName} كما هي. خذ وقتك، وستجد خيار الترقية هنا متى ما كنت مستعداً.',
    'subscription.cancelled.ctaBack': 'العودة إلى الباقات',
    'subscription.cancelled.ctaDashboard': 'الانتقال إلى لوحة التحكم',
    'subscription.cancelled.surveyCaption': 'ما الذي أوقفك؟ (اختياري)',
    'subscription.cancelled.reasonPrice': 'السعر مرتفع',
    'subscription.cancelled.reasonThinking': 'بحاجة للتفكير',
    'subscription.cancelled.reasonFeatures': 'تنقصني ميزات',
    'subscription.cancelled.reasonOther': 'أخرى',
    'subscription.cancelled.surveyThanks': 'شكراً — هذا يساعدنا على التحسين.',
    'subscription.banner.title': 'مرحباً بك في باقة {planName}!',
    'subscription.banner.cta': 'استكشف أدواتك الجديدة ←',
    'subscription.banner.dismissAria': 'إغلاق رسالة الترحيب',
    'subscription.unlock.branchesTitle': 'فروع إضافية',
    'subscription.unlock.branchesSub': 'حتى {newCap} موقعاً',
    // Founder R-5 — Growth tier branches cap is null (unlimited). Mirrors the
    // approved 'عروض مخصّصة بلا حدود' idiom rather than the broken
    // "حتى بلا حدود موقعاً" token-substitution path.
    'subscription.unlock.branchesSubUnlimited': 'فروع بلا حدود',
    'subscription.unlock.staffTitle': 'فريق أكبر',
    'subscription.unlock.staffSub': 'حتى {newCap} موظفاً',
    'subscription.unlock.staffSubUnlimited': 'موظفون بلا حدود',
    // Glossary entry: Promotion → عروض (NOT ترويج — means advertising).
    'subscription.unlock.promotionsTitle': 'العروض',
    'subscription.unlock.promotionsSub': 'عروض مخصّصة بلا حدود',
    'subscription.unlock.analyticsBasicTitle': 'تحليلات أساسية',
    'subscription.unlock.analyticsBasicSub': 'الإيرادات والحجوزات وأكثر الخدمات طلباً',
    'subscription.unlock.analyticsAdvTitle': 'تحليلات متقدّمة',
    'subscription.unlock.analyticsAdvSub': 'الفئات والاحتفاظ والتنبّؤ',
    'subscription.unlock.loyaltyTitle': 'برنامج الولاء',
    'subscription.unlock.loyaltySub': 'نقاط ومستويات ومكافآت',
    'subscription.unlock.rbacTitle': 'الأدوار والصلاحيات',
    'subscription.unlock.rbacSub': 'تحكّم دقيق بصلاحيات الموظفين',
    'subscription.cap.branchesTitle': 'جاهز للفرع رقم {n}؟',
    // Use حتى + accusative singular (فرعاً / موظفاً) — invariant across numbers
    // (avoids the 3-10 vs 11-99 plural/singular agreement issue).
    'subscription.cap.branchesBody': 'تتيح باقة {plan} حتى {cap} فرعاً. الترقية إلى {nextPlan} تتيح حتى {nextCap} فرعاً — لكل فرع ساعاته وموظفوه وحجوزاته.',
    'subscription.cap.branchesBodyUnlimited': 'تتيح باقة {plan} حتى {cap} فرعاً. الترقية إلى {nextPlan} تتيح عدداً غير محدود من الفروع — لكل فرع ساعاته وموظفوه وحجوزاته.',
    'subscription.cap.staffTitle': 'جاهز لعضو الفريق رقم {n}؟',
    'subscription.cap.staffBody': 'تتيح باقة {plan} حتى {cap} موظفاً. الترقية إلى {nextPlan} تتيح إضافة حتى {nextCap} موظفاً.',
    'subscription.cap.staffBodyUnlimited': 'تتيح باقة {plan} حتى {cap} موظفاً. الترقية إلى {nextPlan} تتيح إضافة عدد غير محدود من الموظفين.',
    'subscription.cap.cta': 'مقارنة الباقات ←',
    'subscription.recommendedRibbon': 'موصى به لك',
    'subscription.enterprise.title': 'لنصمّم باقتك الخاصة بالمؤسسات',
    'subscription.enterprise.sub': 'باقات المؤسسات مخصّصة وفق حجم فريقك وعدد فروعك وعمليات الربط. أخبرنا عن أعمالك وسيتواصل فريقنا خلال يوم عمل واحد.',
    'subscription.enterprise.branchesField': 'العدد التقديري للفروع',
    'subscription.enterprise.staffField': 'العدد التقديري للفريق',
    'subscription.enterprise.messageField': 'هل ثمة معلومات إضافية؟ (اختياري)',
    'subscription.enterprise.submit': 'اطلب اتصالاً',
    'subscription.enterprise.cancel': 'إلغاء',
    'subscription.enterprise.successTitle': 'شكراً — سيتواصل معك فريق المبيعات خلال يوم عمل واحد.',
    'subscription.enterprise.successClose': 'إغلاق',

    // ── AB#1982 — Owner Dashboard AR/EN i18n coverage ─────────────────────
    // DRAFT — pending arabic-translator committee sign-off (AB#1982 AC-2).
    'dashboard.greeting': 'مرحباً، {name}',
    'dashboard.subtitle': 'إليك ما يحدث {period}',
    'dashboard.period.today': 'اليوم',
    'dashboard.period.week': 'هذا الأسبوع',
    'dashboard.period.month': 'هذا الشهر',
    'dashboard.period.overall': 'بشكل عام',
    'dashboard.toast.refreshSuccess': 'تم تحديث لوحة التحكم بنجاح.',
    'dashboard.refresh': 'تحديث',
    'dashboard.refreshing': 'جارٍ التحديث...',
    'dashboard.loading': 'جارٍ تحميل البيانات…',
    'dashboard.loadFailed': 'تعذّر تحميل أرقام لوحة التحكم.',
    'dashboard.retry': 'إعادة المحاولة',
    'dashboard.stat.cancelledOrNoShow': 'ملغى أو لم يحضر',
    'dashboard.metric.totalPaymentRevenue': 'إجمالي إيرادات المدفوعات',
    'dashboard.stat.totalAppointments': 'إجمالي المواعيد',
    'dashboard.stat.employees': 'الموظفون',
    'dashboard.stat.activeBranches': 'الفروع النشطة',
    'dashboard.chart.bookings': 'الحجوزات',
    'dashboard.chart.amount': 'المبلغ',
    'dashboard.chart.totalRevenue': 'إجمالي الإيرادات',
    'dashboard.chart.reservationRate': 'معدل الحجز',
    'dashboard.dateRange.allTime': 'كل الأوقات',
    'dashboard.dateRange.today': 'اليوم',
    'dashboard.dateRange.last7days': 'آخر 7 أيام',
    'dashboard.dateRange.last30days': 'آخر 30 يوماً',
    'dashboard.dateRange.date': 'التاريخ',
    'dashboard.dateRange.ariaLabel': 'تصفية حسب النطاق الزمني',
    // Owner Payments page (AB#2060) — pay-at-salon. Western numerals kept for
    // money, matching the dashboard convention. Pending arabic-translator +
    // content-review-committee sign-off.
    'payments.title': 'المدفوعات',
    'payments.subtitle.payAtSalon': 'المدفوعات المُحصّلة في صالونك',
    'payments.subtitle.online': 'إدارة المعاملات والعمولات والتحويلات',
    'payments.tab.transactions': 'المعاملات',
    'payments.tab.commissions': 'العمولات',
    'payments.tab.payouts': 'التحويلات',
    'payments.retry': 'إعادة المحاولة',
    'payments.summary.totalRevenue': 'إجمالي الإيرادات',
    'payments.summary.transactions': 'المعاملات',
    'payments.filter.status': 'حالة الدفع',
    'payments.filter.from': 'من',
    'payments.filter.to': 'إلى',
    'payments.filter.clear': 'مسح عوامل التصفية',
    'payments.col.date': 'التاريخ',
    'payments.col.appointment': 'الموعد',
    'payments.col.stripePi': 'معرّف Stripe',
    'payments.col.amount': 'المبلغ',
    'payments.col.status': 'الحالة',
    'payments.col.method': 'طريقة الدفع',
    'payments.empty.title': 'لا توجد مدفوعات بعد',
    'payments.empty.body': 'ستظهر هنا المدفوعات التي تسجّلها في صالونك.',
    'calendar.selectDate': 'اختر تاريخاً',
    'calendar.all': 'الكل',
  },
};

/* ---------------------------------------------------------------------------
 * Mapping from navConfig labels to translation keys
 * -------------------------------------------------------------------------*/

const labelToKey: Record<string, string> = {
  Dashboard: 'nav.dashboard',
  Appointments: 'nav.appointments',
  Services: 'nav.services',
  Employees: 'nav.employees',
  Promotions: 'nav.promotions',
  Customers: 'nav.customers',
  Payments: 'nav.payments',
  Subscription: 'nav.subscription',
  Loyalty: 'nav.loyalty',
  Inventory: 'nav.inventory',
  Referrals: 'nav.referrals',
  Settings: 'nav.settings',
  Profile: 'nav.profile',
  Users: 'nav.users',
  Owners: 'nav.owners',
  'Booking Support': 'nav.bookingSupport',
  'Feature Flags': 'nav.featureFlags',
  'Audit Log': 'nav.auditLog',
  'API Keys': 'nav.apiKeys',
  'Sponsored Placements': 'nav.sponsoredPlacements',
  'Platform Promotions': 'nav.platformPromotions',
  Categories: 'nav.categories',
  Calendar: 'nav.calendar',
  'My Business': 'nav.myBusiness',
  'Gender Overrides': 'nav.genderOverrides',
  Chat: 'nav.chat',
  Help: 'nav.help',
  Support: 'nav.support',
};

/* ---------------------------------------------------------------------------
 * Context types
 * -------------------------------------------------------------------------*/

export type Language = 'en' | 'ar';

interface LanguageContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  /** Translate a navConfig label (e.g. "Dashboard") to the current language */
  tNav: (label: string) => string;
  isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

const STORAGE_KEY = 'devanza_language';

/* ---------------------------------------------------------------------------
 * Provider
 * -------------------------------------------------------------------------*/

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'ar' || stored === 'en') return stored;
    } catch {
      // localStorage may be unavailable
    }
    return 'en';
  });

  const applyLanguage = useCallback((lang: Language) => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  }, []);

  // Apply on mount and when language changes
  useEffect(() => {
    applyLanguage(language);
  }, [language, applyLanguage]);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // ignore
    }
    // Sync i18next so useTranslation() picks up the new language
    i18nChangeLanguage(lang).catch(() => {/* silently ignore */});
  }, []);

  const t = useCallback(
    (key: string): string => {
      return translations[language]?.[key] ?? translations.en?.[key] ?? key;
    },
    [language],
  );

  const tNav = useCallback(
    (label: string): string => {
      const key = labelToKey[label];
      if (!key) return label;
      return translations[language]?.[key] ?? label;
    },
    [language],
  );

  const isRTL = language === 'ar';

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, tNav, isRTL }}>
      {children}
    </LanguageContext.Provider>
  );
};

/* ---------------------------------------------------------------------------
 * Hook
 * -------------------------------------------------------------------------*/

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return ctx;
}
